import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import {
  Users, Briefcase, CalendarDays, IndianRupee, Trophy, Shield, Zap,
  BarChart3, Star, ArrowRight, CheckCircle, Globe,
  Target, Award, Dumbbell, ClipboardCheck, FileText, Brain,
  Play, ChevronDown, Menu, X, Send, Loader2, Mail, Smartphone,
  Bot, RefreshCw, MapPin, AlarmClock, Sparkles, Clock, Radio,
  Video, Wand2, Film, Milestone, Share2, TrendingUp,
  Quote, MessageCircle, Eye, Volume2, Signal,
} from 'lucide-react';
import { LOGO_URL, BrandText } from '@/lib/brand';
import { toast } from 'sonner';
import api from '@/lib/api';
// Phase 282e — Public Star Academy social proof (auto-hides on cold deploy)
import StarAcademyProofStrip from '@/components/landing/StarAcademyProofStrip';
// Phase 283 — Identity Resolution: thread session_id through public forms
import { getTelemetrySessionId } from '@/hooks/useWebTelemetry';

function useCounter(end, duration = 2000) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  useEffect(() => {
    if (!isInView) return;
    let s = 0;
    const step = Math.max(1, Math.floor(end / (duration / 16)));
    const t = setInterval(() => { s += step; if (s >= end) { setCount(end); clearInterval(t); } else setCount(s); }, 16);
    return () => clearInterval(t);
  }, [isInView, end, duration]);
  return { count, ref };
}

function Anim({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  const v = useInView(ref, { once: true, margin: '-60px' });
  return <motion.div ref={ref} initial={{ opacity: 0, y: 24 }} animate={v ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay, ease: 'easeOut' }} className={className}>{children}</motion.div>;
}

// Phase 648 — Isolated metric card so `useCounter` obeys Rules of Hooks
// (one hook call per component instance instead of inside a .map callback).
function MetricCard({ value, prefix = '', suffix = '', label, decimals = 0, delay = 0 }) {
  const { count, ref } = useCounter(value, 1500 + delay * 100);
  const displayNum = decimals ? Number(count).toFixed(decimals) : Math.floor(count).toLocaleString('en-IN');
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: delay * 0.08, ease: [0.16, 1, 0.3, 1] }}
      whileHover={{ y: -4 }}
      className="relative rounded-2xl backdrop-blur-xl bg-white border border-slate-200/70 hover:border-emerald-500/40 hover:bg-slate-50 transition-colors p-6 lg:p-8 text-center overflow-hidden group"
      data-testid={`metric-card-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div aria-hidden className="absolute inset-x-0 -top-16 h-32 bg-emerald-100/70 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      <div className="relative text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 mb-2">
        {prefix}<span data-testid={`metric-value-${label.toLowerCase().replace(/\s+/g, '-')}`}>{displayNum}</span>{suffix}
      </div>
      <div className="text-[12px] font-medium text-slate-600 uppercase tracking-[0.12em]">{label}</div>
    </motion.div>
  );
}

function ContactForm({ source = 'contact_form', onSuccess, buttonText = 'Send Message' }) {
  const [f, setF] = useState({ name: '', email: '', phone: '', academy_name: '', message: '' });
  const [s, setS] = useState(false);
  const h = async (e) => {
    e.preventDefault();
    if (!f.name || !f.email) { toast.error('Name and email are required'); return; }
    setS(true);
    try {
      // Phase 283 — forward telemetry session_id so backend can merge this
      // form submission with the visitor's prior anonymous beacons.
      const session_id = getTelemetrySessionId() || undefined;
      const r = await api.post('/public/contact', { ...f, source, session_id });
      toast.success(r.data.message);
      setF({ name: '', email: '', phone: '', academy_name: '', message: '' });
      if (onSuccess) onSuccess();
    }
    catch { toast.error('Failed to send.'); } finally { setS(false); }
  };
  return (
    <form onSubmit={h} className="space-y-3" data-testid="landing-contact-form">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1"><Label className="text-xs">Name *</Label><Input data-testid="landing-contact-name" value={f.name} onChange={e => setF({...f, name: e.target.value})} placeholder="Your name" required /></div>
        <div className="space-y-1"><Label className="text-xs">Email *</Label><Input data-testid="landing-contact-email" type="email" value={f.email} onChange={e => setF({...f, email: e.target.value})} placeholder="you@academy.com" required /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1"><Label className="text-xs">Phone</Label><Input data-testid="landing-contact-phone" value={f.phone} onChange={e => setF({...f, phone: e.target.value})} placeholder="+91 98765..." /></div>
        <div className="space-y-1"><Label className="text-xs">Academy</Label><Input data-testid="landing-contact-academy" value={f.academy_name} onChange={e => setF({...f, academy_name: e.target.value})} placeholder="Your academy" /></div>
      </div>
      <div className="space-y-1"><Label className="text-xs">Message</Label><Textarea data-testid="landing-contact-message" value={f.message} onChange={e => setF({...f, message: e.target.value})} placeholder="Tell us about your academy..." rows={3} /></div>
      <Button type="submit" data-testid="landing-contact-submit" className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={s}>{s ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}{s ? 'Sending...' : buttonText}</Button>
    </form>
  );
}

const features = [
  { icon: Users, title: 'Student Management', desc: 'Enroll, track attendance, fees & progress cards.', gradient: 'from-emerald-500 to-teal-600' },
  { icon: Briefcase, title: 'Smart HRMS', desc: 'Payroll, leaves & automated calculations.', gradient: 'from-blue-500 to-indigo-600' },
  { icon: CalendarDays, title: 'Event Management', desc: 'AI posters, QR registration & certificates.', gradient: 'from-amber-500 to-orange-600' },
  { icon: IndianRupee, title: 'AI Invoicing', desc: 'GST invoices, 99.2% duplicate detection.', gradient: 'from-green-500 to-emerald-600' },
  { icon: BarChart3, title: 'Analytics & KPI', desc: 'Revenue dashboards & AI recommendations.', gradient: 'from-purple-500 to-violet-600' },
  { icon: Shield, title: 'Enterprise Security', desc: 'RBAC, audit logs & encrypted data.', gradient: 'from-slate-500 to-zinc-600' },
  { icon: Target, title: 'CRM & Leads', desc: 'Track inquiries & convert prospects.', gradient: 'from-rose-500 to-pink-600' },
  { icon: Smartphone, title: 'Mobile Ready', desc: 'Full responsive for coaches & parents.', gradient: 'from-cyan-500 to-sky-600' },
  { icon: Award, title: 'Progress Cards', desc: 'Skill ratings, coach notes & PDF reports.', gradient: 'from-yellow-500 to-amber-600' },
  { icon: Dumbbell, title: 'Fitness Tracking', desc: 'BMI auto-calc & performance benchmarks.', gradient: 'from-red-500 to-rose-600' },
  { icon: ClipboardCheck, title: 'Attendance System', desc: 'QR marking, streaks & gamification.', gradient: 'from-teal-500 to-green-600' },
  { icon: Brain, title: 'AI Insights', desc: 'Revenue forecasts & smart scheduling.', gradient: 'from-fuchsia-500 to-purple-600' },
];

export default function Landing() {
  const [mob, setMob] = useState(false);
  const [sticky, setSticky] = useState(false);
  const [dlg, setDlg] = useState(false);
  const [aiSuiteOpen, setAiSuiteOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [scrolledNav, setScrolledNav] = useState(false);
  // Phase 255-AddonsRoadmap — landing-page waitlist capture for the upcoming
  // SocialX + StudioX Legend-tier AI add-ons.
  const [addonEmail, setAddonEmail] = useState('');
  const [addonSubmitting, setAddonSubmitting] = useState(false);
  const [addonSubmitted, setAddonSubmitted] = useState(false);
  // Phase 648 — Premium hero video modal state (opens youtu.be/ABOfFEaShZU inline)
  const [videoOpen, setVideoOpen] = useState(false);
  // Phase 2 (Landing redesign) — premium pricing monthly/annual toggle + footer newsletter capture
  const [annualBilling, setAnnualBilling] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterSubmitting, setNewsletterSubmitting] = useState(false);
  const [newsletterSubmitted, setNewsletterSubmitted] = useState(false);

  const submitNewsletter = async (e) => {
    e?.preventDefault?.();
    const trimmed = (newsletterEmail || '').trim();
    if (!trimmed) return;
    setNewsletterSubmitting(true);
    try {
      // Reuse the addons/waitlist backend endpoint — the source field distinguishes footer signups.
      await api.post('/addons/waitlist', { email: trimmed, addon: 'newsletter', source: 'landing_footer_newsletter' });
      setNewsletterSubmitted(true);
      setNewsletterEmail('');
      toast.success('You\u2019re on the list! Watch your inbox for product news.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not subscribe \u2014 please try again.');
    } finally {
      setNewsletterSubmitting(false);
    }
  };

  const submitAddonWaitlist = async (e) => {
    e?.preventDefault?.();
    const trimmed = (addonEmail || '').trim();
    if (!trimmed) return;
    setAddonSubmitting(true);
    try {
      const res = await api.post('/addons/waitlist', { email: trimmed, addon: 'both', source: 'landing_pricing' });
      setAddonSubmitted(true);
      if (res?.data?.already_subscribed) {
        toast.info('You\u2019re already on the list \u2014 we\u2019ll notify you when SocialX & StudioX go live!');
      } else {
        toast.success('You\u2019re on the list! We\u2019ll email you the moment the beta opens.');
      }
      setAddonEmail('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save your email \u2014 please try again.');
    } finally {
      setAddonSubmitting(false);
    }
  };
  useEffect(() => {
    const h = () => {
      const y = window.scrollY;
      setSticky(y > 500);
      setScrolledNav(y > 60);
    };
    window.addEventListener('scroll', h, { passive: true });
    return () => window.removeEventListener('scroll', h);
  }, []);
  // Phase 151 — Warm Attribution Loop. When a blasted academy lands via the
  // outbound CTA (?src=outbound_intel&lead_id=XYZ), fire a non-blocking
  // telemetry POST so the Super Admin CRM sees them flip to "Warm Engaged".
  // Phase 631 — Extended with a 30s heartbeat so we can compute the
  // total-seconds-on-site engagement score visible on the Agency CRM.
  useEffect(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      const src = sp.get('src');
      const leadId = sp.get('lead_id');
      if (!src || !leadId || src.toLowerCase() !== 'outbound_intel') return;
      // Persist for cross-page navigation so the heartbeat continues if
      // the prospect clicks into /features, /pricing, etc. from Landing.
      try { sessionStorage.setItem('outplay_utm_lead', leadId); } catch (_e) { /* private mode */ }
      // Initial click ping
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/telemetry/click`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lead_id: leadId,
          src,
          landing_path: window.location.pathname || '/',
          referrer: document.referrer || '',
        }),
        keepalive: true,
      }).catch(() => { /* silent */ });

      // 30s heartbeat while the tab is visible
      const HEARTBEAT_SECS = 30;
      let last = Date.now();
      const beat = () => {
        if (document.visibilityState !== 'visible') { last = Date.now(); return; }
        const elapsed = Math.min(60, Math.round((Date.now() - last) / 1000));
        last = Date.now();
        if (elapsed <= 0) return;
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/telemetry/heartbeat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ lead_id: leadId, seconds: elapsed }),
          keepalive: true,
        }).catch(() => { /* silent */ });
      };
      const timer = setInterval(beat, HEARTBEAT_SECS * 1000);
      const onHide = () => { if (document.visibilityState === 'hidden') beat(); };
      document.addEventListener('visibilitychange', onHide);
      window.addEventListener('beforeunload', beat);
      return () => {
        clearInterval(timer);
        document.removeEventListener('visibilitychange', onHide);
        window.removeEventListener('beforeunload', beat);
      };
    } catch (_e) { /* never block the page on a telemetry failure */ }
  }, []);
  // Phase 140 — SEO metadata for the landing page (head-mutation via DOM API
  // since this project doesn't include react-helmet). Sets title +
  // meta[name=description] when the page mounts; restores defaults on unmount.
  useEffect(() => {
    const prevTitle = document.title;
    document.title = 'Out-Play | #1 Sports Academy Management App, Turf Booking & StreamX Streaming';
    const metas = [
      { name: 'description', content: 'Out-Play | India\'s #1 Sports Academy Management Software. Scale your academy 10× with automated invoicing, ArenaX turf bookings at an industry-leading 3.5% flat marketplace fee, AI-driven StreamX live streaming, replay vault, video analysis, and the commission-free Legend Tier at ₹1,998/month.' },
      { name: 'keywords', content: 'sports academy management app, turf booking software, sports live streaming, academy crm, parent invoicing, coaching platform, broadcasting tools, league fixture automation, pay and play turf, video analysis, ArenaX, StreamX' },
      { property: 'og:title', content: 'Out-Play | Sports Academy SaaS with ArenaX + StreamX bundled' },
      { property: 'og:description', content: 'Pro + ArenaX + StreamX + Video Analysis for ₹1,998/month. Industry-leading 3.5% flat marketplace fee for independent facility hosts. Legend Tier = 0% commission, forever.' },
      { property: 'og:type', content: 'website' },
    ];
    const created = [];
    metas.forEach(({ name, property, content }) => {
      const selector = name ? `meta[name="${name}"]` : `meta[property="${property}"]`;
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        if (name) el.setAttribute('name', name);
        if (property) el.setAttribute('property', property);
        document.head.appendChild(el);
        created.push(el);
      }
      el.setAttribute('content', content);
    });
    return () => {
      document.title = prevTitle;
      created.forEach((el) => el.remove());
    };
  }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      {/* Phase 642 — Sticky mobile CTA bar (appears after scrolling past hero) */}
      <div className={`sm:hidden fixed bottom-0 inset-x-0 z-40 border-t border-slate-200 bg-white/95 backdrop-blur-lg shadow-2xl transition-transform duration-300 ${scrolledNav ? 'translate-y-0' : 'translate-y-full'}`}
           data-testid="sticky-mobile-cta">
        <div className="flex items-center gap-2 p-3">
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-semibold text-slate-900 truncate">Start your 14-day free trial</p>
            <p className="text-[10px] text-slate-500">No credit card · UPI + Razorpay</p>
          </div>
          <Link to="/signup" className="flex-shrink-0">
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-500 text-white h-9 px-4 rounded-lg font-semibold shadow-md"
                    data-testid="sticky-cta-btn">
              Start free <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
      {/* NAV — Phase 145 Premium Header (Products mega-menu) */}
      <nav className={`sticky top-0 z-50 border-b transition-all duration-300 ${scrolledNav ? 'border-slate-200/90 shadow-sm shadow-slate-900/5' : 'border-slate-200/40 dark:border-slate-800/40'}`} data-testid="landing-navbar"
        style={{ background: scrolledNav
          ? 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.98) 100%)'
          : 'linear-gradient(135deg, rgba(255,255,255,0.92) 0%, rgba(248,250,252,0.92) 100%)', backdropFilter: 'blur(20px) saturate(180%)' }}>
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6 lg:px-8 h-[72px]">
          {/* Logo — breathing room + crisp anchor + scroll-reveal emerald accent */}
          <Link to="/" className="flex flex-col items-start group focus:outline-none" data-testid="landing-nav-logo">
            <div className="flex items-center gap-2.5">
              <div className="relative">
                <img src={LOGO_URL} alt="Out-Play" className="h-10 w-10 rounded-xl object-cover shadow-sm ring-2 ring-emerald-500/30 group-hover:ring-emerald-500/40 transition-all duration-200" />
                <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 bg-emerald-500 rounded-full border-2 border-white" />
              </div>
              <span className="font-display font-extrabold text-2xl tracking-tight">
                <span className="text-slate-800 dark:text-foreground">Out</span><span className="text-red-500">-Play</span>
              </span>
            </div>
            <div data-testid="landing-nav-logo-accent" className={`h-[2px] bg-emerald-500 rounded-full transition-all duration-300 origin-left ${scrolledNav ? 'w-full opacity-100 mt-1' : 'w-0 opacity-0 mt-0'}`} />
          </Link>

          {/* Desktop Nav — clean link row + Products mega-menu */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="landing-nav-desktop">
            {/* Products mega-menu (hover-triggered) */}
            <div className="relative" onMouseEnter={() => setProductsOpen(true)} onMouseLeave={() => setProductsOpen(false)}>
              <button
                data-testid="landing-nav-products-trigger"
                className="flex items-center gap-1 text-sm font-semibold text-slate-700 hover:text-slate-900 py-6 transition-colors focus:outline-none"
                aria-expanded={productsOpen}
                aria-haspopup="true"
              >
                Products
                <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${productsOpen ? 'rotate-180' : ''}`} />
              </button>
              <div
                data-testid="landing-nav-products-menu"
                role="menu"
                aria-label="Out-Play Products"
                className={`absolute top-full left-1/2 -translate-x-1/2 w-[500px] bg-white border border-slate-200 rounded-2xl shadow-2xl shadow-slate-900/10 p-3 grid grid-cols-1 gap-1 transition-all duration-200 ${productsOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2 pointer-events-none'}`}
              >
                <Link to="/arenax" role="menuitem" data-testid="landing-nav-products-arenax" className="flex items-start gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors group/item">
                  <div className="h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #0891b2, #f59e0b)' }}>
                    <Sparkles className="h-5 w-5 text-slate-900" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                      ArenaX Facility Engine
                      <span className="text-[9px] font-black bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded uppercase tracking-wider">New</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">Turf bookings, automated brackets, fixture blasts. 3.5% flat — 0% for Legend Tier.</p>
                  </div>
                </Link>
                <Link to="/streamx" role="menuitem" data-testid="landing-nav-products-livex" className="flex items-start gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors group/item">
                  <div className="h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0 relative" style={{ background: 'linear-gradient(135deg, #dc2626, #f97316)' }}>
                    <Radio className="h-5 w-5 text-slate-900" />
                    <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
                      <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500 border-2 border-white" />
                    </span>
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                      StreamX AI Broadcast
                      <span className="inline-flex items-center gap-1 text-[9px] font-black bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded uppercase tracking-wider">
                        <span className="w-1 h-1 rounded-full bg-rose-500 animate-pulse" /> Live
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">Automated match streams, replay vault, video analysis. Powered by LiveKit.</p>
                  </div>
                </Link>
                <Link to="/features" role="menuitem" data-testid="landing-nav-products-academy" className="flex items-start gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors group/item">
                  <div className="h-10 w-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
                    <BarChart3 className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900">Academy Management Hub</div>
                    <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">Full HRMS, automated parent invoicing, payroll, attendance, fee collection.</p>
                  </div>
                </Link>
              </div>
            </div>

            <a href="#pricing" data-testid="landing-nav-pricing" className="text-sm font-semibold text-slate-700 hover:text-slate-900 transition-colors">Pricing</a>
            <Link to="/faq" data-testid="landing-nav-faq" className="text-sm font-semibold text-slate-700 hover:text-slate-900 transition-colors">FAQ</Link>
            <a href="#contact" data-testid="landing-nav-contact" className="text-sm font-semibold text-slate-700 hover:text-slate-900 transition-colors">Contact</a>
          </nav>

          {/* Right-side CTAs — clear hierarchy */}
          <div className="hidden lg:flex items-center gap-5">
            <Link to="/login" data-testid="landing-nav-login" className="text-sm font-semibold text-slate-700 hover:text-slate-900 transition-colors">
              Log in
            </Link>
            <Link to="/signup" data-testid="landing-nav-signup">
              <Button className="h-10 px-5 text-sm font-semibold bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 shadow-md shadow-emerald-600/20 hover:shadow-lg hover:shadow-emerald-500/25 hover:-translate-y-0.5 transition-all duration-200 rounded-xl">
                Start Free Trial <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
            </Link>
          </div>

          {/* Mobile menu toggle */}
          <button data-testid="landing-nav-mobile-toggle" className="lg:hidden p-2 rounded-xl hover:bg-slate-100 transition-colors" onClick={() => setMob(!mob)}>
            {mob ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mob && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="lg:hidden border-t border-slate-200/70 bg-white px-4 py-3 space-y-1" data-testid="landing-nav-mobile">
            {/* Products section header */}
            <div className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-400 px-3 pt-2 pb-1">Products</div>
            <Link to="/arenax" onClick={() => setMob(false)} data-testid="landing-mobile-nav-arenax" className="flex items-center gap-3 py-2.5 px-3 rounded-xl hover:bg-slate-50 transition-colors">
              <span className="h-9 w-9 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0891b2, #f59e0b)' }}>
                <Sparkles className="h-5 w-5 text-slate-900" />
              </span>
              <span className="flex items-center gap-2">
                <span className="text-sm font-semibold text-slate-800">ArenaX</span>
                <span className="text-[9px] font-black bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded uppercase tracking-wider">New</span>
              </span>
            </Link>
            <Link to="/streamx" onClick={() => setMob(false)} data-testid="landing-mobile-nav-livex" className="flex items-center gap-3 py-2.5 px-3 rounded-xl hover:bg-slate-50 transition-colors">
              <span className="h-9 w-9 rounded-xl flex items-center justify-center relative" style={{ background: 'linear-gradient(135deg, #dc2626, #f97316)' }}>
                <Radio className="h-5 w-5 text-slate-900" />
                <span className="absolute -top-0.5 -right-0.5 h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
                </span>
              </span>
              <span className="flex items-center gap-2">
                <span className="text-sm font-semibold text-slate-800">StreamX</span>
                <span className="inline-flex items-center gap-1 text-[9px] font-black bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded uppercase tracking-wider">
                  <span className="w-1 h-1 rounded-full bg-rose-500 animate-pulse" /> Live
                </span>
              </span>
            </Link>
            <Link to="/features" onClick={() => setMob(false)} data-testid="landing-mobile-nav-academy" className="flex items-center gap-3 py-2.5 px-3 rounded-xl hover:bg-slate-50 transition-colors">
              <span className="h-9 w-9 rounded-xl bg-emerald-100 flex items-center justify-center">
                <BarChart3 className="h-5 w-5 text-emerald-700" />
              </span>
              <span className="text-sm font-semibold text-slate-800">Academy Hub</span>
            </Link>

            <div className="border-t border-slate-100 my-2" />

            <a href="#pricing" onClick={() => setMob(false)} data-testid="landing-mobile-nav-pricing" className="flex items-center gap-3 py-2.5 px-3 rounded-xl hover:bg-slate-50 transition-colors">
              <span className="h-9 w-9 rounded-xl bg-amber-100 flex items-center justify-center"><IndianRupee className="h-5 w-5 text-amber-600" /></span>
              <span className="text-sm font-semibold text-slate-700">Pricing</span>
            </a>
            <Link to="/faq" onClick={() => setMob(false)} data-testid="landing-mobile-nav-faq" className="flex items-center gap-3 py-2.5 px-3 rounded-xl hover:bg-slate-50 transition-colors">
              <span className="h-9 w-9 rounded-xl bg-blue-100 flex items-center justify-center"><Brain className="h-5 w-5 text-blue-600" /></span>
              <span className="text-sm font-semibold text-slate-700">FAQ</span>
            </Link>
            <a href="#contact" onClick={() => setMob(false)} data-testid="landing-mobile-nav-contact" className="flex items-center gap-3 py-2.5 px-3 rounded-xl hover:bg-slate-50 transition-colors">
              <span className="h-9 w-9 rounded-xl bg-rose-100 flex items-center justify-center"><Mail className="h-5 w-5 text-rose-600" /></span>
              <span className="text-sm font-semibold text-slate-700">Contact</span>
            </a>

            <div className="flex gap-2 pt-3 pb-1">
              <Link to="/login" onClick={() => setMob(false)} data-testid="landing-mobile-nav-login" className="flex-1"><Button variant="outline" className="w-full h-10 rounded-xl text-sm font-semibold">Log in</Button></Link>
              <Link to="/signup" onClick={() => setMob(false)} data-testid="landing-mobile-nav-signup" className="flex-1"><Button className="w-full h-10 rounded-xl text-sm font-semibold bg-gradient-to-r from-emerald-600 to-teal-600">Free Trial</Button></Link>
            </div>
          </motion.div>
        )}
      </nav>

      {/* ===== PREMIUM HERO — Phase 648 ===== */}
      <section id="hero" className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden pt-28 pb-20 bg-gradient-to-b from-white via-[#FAFAFB] to-[#F5F7FA]">
        {/* Aurora + product micro-cards + academy-life photo strip */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {/* Emerald aurora glows (softened for light canvas) */}
          <motion.div
            aria-hidden
            className="absolute top-[-8rem] left-1/4 w-[900px] h-[900px] rounded-full bg-emerald-200/50 blur-[160px]"
            animate={{ opacity: [0.5, 0.8, 0.5] }}
            transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.div
            aria-hidden
            className="absolute bottom-[-6rem] right-1/4 w-[720px] h-[720px] rounded-full bg-teal-200/40 blur-[140px]"
            animate={{ opacity: [0.4, 0.7, 0.4] }}
            transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
          />
          {/* Grid overlay — subtle slate lines */}
          <div className="absolute inset-0 opacity-[0.04] bg-[linear-gradient(rgba(15,23,42,0.5)_1px,transparent_1px),linear-gradient(90deg,rgba(15,23,42,0.5)_1px,transparent_1px)] bg-[size:64px_64px]" />

          {/* Academy-life photo strip — wide low-opacity band along the bottom */}
          <div className="absolute inset-x-0 bottom-0 h-64 sm:h-80 pointer-events-none">
            <img
              src="https://images.pexels.com/photos/8007406/pexels-photo-8007406.jpeg?auto=compress&cs=tinysrgb&w=1920"
              alt=""
              className="absolute inset-0 w-full h-full object-cover object-center opacity-[0.10] saturate-50"
              loading="eager"
            />
            {/* Fade the photo edges seamlessly into the light canvas */}
            <div className="absolute inset-0 bg-gradient-to-t from-[#FAFAFB] via-[#FAFAFB]/70 to-transparent" />
          </div>

          {/* Bottom vignette — light fade to canvas */}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_45%,#FAFAFB_100%)]" />

          {/* Product micro-cards — decorative depth accents. Hidden on
              small screens so mobile hero stays clean. */}
          <motion.div
            aria-hidden
            initial={{ opacity: 0, y: 20, rotate: -3 }}
            animate={{ opacity: 1, y: 0, rotate: -6 }}
            transition={{ duration: 1.1, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="hidden lg:block absolute top-56 left-4 xl:left-16 w-56 rounded-2xl bg-white border border-slate-200 shadow-[0_16px_40px_-12px_rgba(15,23,42,0.15)] backdrop-blur-xl p-3.5"
            data-testid="hero-micro-attendance"
          >
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-lg bg-emerald-100 border border-emerald-200">
                <ClipboardCheck className="h-3.5 w-3.5 text-emerald-700" />
              </span>
              <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-[0.12em] whitespace-nowrap">Attendance · U-15</span>
            </div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-2xl font-bold tabular-nums text-slate-900">47</span>
              <span className="text-[11px] text-slate-500">/ 50 present</span>
            </div>
            <div className="mt-2 h-1 w-full rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full w-[94%] bg-emerald-500 rounded-full" />
            </div>
            <p className="text-[10px] text-emerald-700 font-medium mt-1.5 flex items-center gap-1">
              <CheckCircle className="h-3 w-3" /> Auto-synced to parent WhatsApp
            </p>
          </motion.div>

          <motion.div
            aria-hidden
            initial={{ opacity: 0, y: 20, rotate: 3 }}
            animate={{ opacity: 1, y: 0, rotate: 5 }}
            transition={{ duration: 1.1, delay: 1.1, ease: [0.16, 1, 0.3, 1] }}
            className="hidden xl:block absolute bottom-40 left-8 w-60 rounded-2xl bg-white border border-slate-200 shadow-[0_16px_40px_-12px_rgba(15,23,42,0.15)] backdrop-blur-xl p-3.5"
            data-testid="hero-micro-payment"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-slate-500 uppercase tracking-[0.14em]">
                <IndianRupee className="h-3 w-3" /> Fee · Razorpay
              </span>
              <span className="text-[9px] font-bold text-emerald-700 uppercase tracking-widest bg-emerald-100 border border-emerald-200 rounded px-1.5 py-0.5">Paid</span>
            </div>
            <div className="text-xl font-bold tabular-nums text-slate-900">₹8,500</div>
            <p className="text-[11px] text-slate-500 mt-0.5">Aarav Sharma · U-15 · Monthly</p>
            <div className="mt-2 pt-2 border-t border-slate-100 text-[10px] text-slate-500 flex items-center justify-between">
              <span className="font-mono">TXN #P2AR7X</span>
              <span className="text-emerald-700 font-medium">Receipt sent</span>
            </div>
          </motion.div>

          <motion.div
            aria-hidden
            initial={{ opacity: 0, y: 20, rotate: 4 }}
            animate={{ opacity: 1, y: 0, rotate: 5 }}
            transition={{ duration: 1.1, delay: 1.3, ease: [0.16, 1, 0.3, 1] }}
            className="hidden xl:block absolute top-56 right-4 xl:right-16 w-56 rounded-2xl bg-white border border-slate-200 shadow-[0_16px_40px_-12px_rgba(15,23,42,0.15)] backdrop-blur-xl p-3.5"
            data-testid="hero-micro-live"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="inline-flex items-center gap-1.5">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-500 opacity-75" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-rose-500" />
                </span>
                <span className="text-[10px] font-bold text-rose-600 uppercase tracking-widest">Live</span>
              </span>
              <span className="text-[10px] text-slate-500 tabular-nums">67&apos;</span>
            </div>
            <p className="text-[11px] font-semibold text-slate-900">Champions FC · U-15</p>
            <div className="flex items-baseline gap-2 mt-1">
              <span className="text-2xl font-bold text-emerald-700 tabular-nums">2</span>
              <span className="text-slate-400 text-xs">-</span>
              <span className="text-2xl font-bold text-slate-900 tabular-nums">1</span>
              <span className="text-[11px] text-slate-500 ml-1">Blues FC</span>
            </div>
            <div className="mt-2 pt-2 border-t border-slate-100 flex items-center gap-1.5 text-[10px] text-slate-500">
              <Eye className="h-3 w-3" />
              <span className="tabular-nums font-semibold text-slate-700">1,247</span>
              <span>parents watching</span>
            </div>
          </motion.div>
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Trust marker chip */}
          <motion.div
            className="flex justify-center mb-8"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          >
            <span
              data-testid="hero-india-badge"
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full backdrop-blur-xl bg-slate-50 border border-emerald-500/40 text-emerald-700 text-[11px] font-medium tracking-[0.18em] uppercase"
            >
              <span className="relative flex h-1.5 w-1.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400" />
              </span>
              Trusted by Indian Sports Academies · Made in India
            </span>
          </motion.div>

          {/* Headline — new positioning */}
          <motion.h1
            data-testid="hero-headline"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.75, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="mx-auto text-center font-bold tracking-tight text-slate-900 leading-[1.05] max-w-[900px]"
            style={{ fontSize: 'clamp(2.5rem, 6.5vw, 5rem)' }}
          >
            The Operating System<br />
            for Modern{' '}
            <span className="relative inline-block">
              <span className="relative z-10 text-transparent bg-clip-text bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-600">
                Sports Academies
              </span>
              <span className="absolute inset-x-0 -bottom-1 h-2 bg-emerald-200/80 blur-xl -z-0" />
            </span>.
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            data-testid="hero-subheadline"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-6 mx-auto text-center text-slate-600 text-base sm:text-lg leading-relaxed max-w-[720px]"
          >
            Everything your academy needs—from admissions and fee automation to coaching, tournaments, broadcasting, finance and AI-powered athlete development—in one intelligent platform.
          </motion.p>

          {/* Feature chips (Phase 648) */}
          <motion.div
            data-testid="hero-feature-chips"
            className="mt-8 flex flex-wrap justify-center gap-2 max-w-4xl mx-auto"
            initial="hidden"
            animate="show"
            variants={{ hidden: {}, show: { transition: { staggerChildren: 0.04, delayChildren: 0.35 } } }}
          >
            {[
              { emoji: '💳', label: 'Automated Fees' },
              { emoji: '📅', label: 'Smart Scheduling' },
              { emoji: '👥', label: 'Student CRM' },
              { emoji: '🎥', label: 'AI Video Analysis' },
              { emoji: '🏆', label: 'Tournament Management' },
              { emoji: '📱', label: 'Parent App' },
              { emoji: '📡', label: 'Live Streaming' },
              { emoji: '💰', label: 'Finance' },
              { emoji: '👨‍🏫', label: 'Coach Management' },
              { emoji: '🤖', label: 'AI Assistant' },
            ].map((chip) => (
              <motion.span
                key={chip.label}
                variants={{ hidden: { opacity: 0, y: 8 }, show: { opacity: 1, y: 0 } }}
                whileHover={{ y: -2, scale: 1.03 }}
                transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full backdrop-blur-md bg-slate-50 border border-slate-200/80 text-slate-700 hover:text-slate-900 hover:border-emerald-500/50 hover:bg-emerald-500/[0.14] transition-colors duration-200 text-[13px] font-medium cursor-default"
                data-testid={`hero-chip-${chip.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                <span aria-hidden className="text-sm leading-none">{chip.emoji}</span>
                {chip.label}
              </motion.span>
            ))}
          </motion.div>

          {/* CTAs */}
          <motion.div
            className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55 }}
          >
            <Link to="/signup" className="w-full sm:w-auto">
              <motion.button
                data-testid="hero-primary-cta"
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
                transition={{ duration: 0.15 }}
                className="group relative w-full sm:w-auto inline-flex items-center justify-center gap-2 h-14 px-8 rounded-2xl font-semibold text-[15px] text-white bg-emerald-600 hover:bg-emerald-500 shadow-[0_10px_30px_-8px_rgba(16,185,129,0.35)] hover:shadow-[0_16px_44px_-8px_rgba(16,185,129,0.5)] transition-all duration-300"
              >
                Start Free — 14 Days
                <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
              </motion.button>
            </Link>

            <motion.button
              type="button"
              onClick={() => setVideoOpen(true)}
              data-testid="hero-secondary-cta"
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
              transition={{ duration: 0.15 }}
              className="group w-full sm:w-auto inline-flex items-center justify-center gap-2.5 h-14 px-7 rounded-2xl font-semibold text-[15px] text-slate-900 backdrop-blur-xl bg-slate-100/70 hover:bg-slate-100 border border-slate-200 hover:border-slate-300 transition-colors duration-200"
            >
              <span className="flex items-center justify-center w-7 h-7 rounded-full bg-slate-100 group-hover:bg-emerald-100 transition-colors">
                <Play className="h-3 w-3 fill-slate-900 text-slate-900 ml-0.5" />
              </span>
              Watch 60-second Product Tour
            </motion.button>
          </motion.div>

          {/* "No credit card" subtitle under CTA */}
          <motion.p
            className="mt-3 text-center text-[12px] text-slate-500"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.7 }}
          >
            No credit card required · Cancel any time
          </motion.p>

          {/* Trust bar — glass badges */}
          <motion.div
            data-testid="hero-trust-bar"
            className="mt-12 flex flex-wrap justify-center gap-2.5"
            initial="hidden"
            animate="show"
            variants={{ hidden: {}, show: { transition: { staggerChildren: 0.06, delayChildren: 0.8 } } }}
          >
            {[
              { icon: Brain, label: 'AI Powered' },
              { icon: Smartphone, label: 'WhatsApp Integrated' },
              { icon: IndianRupee, label: 'Razorpay Payments' },
              { icon: Shield, label: 'Secure Cloud' },
              { icon: Trophy, label: 'Built for Indian Sports Academies' },
            ].map(({ icon: Icon, label }) => (
              <motion.span
                key={label}
                variants={{ hidden: { opacity: 0, y: 6 }, show: { opacity: 1, y: 0 } }}
                whileHover={{ y: -1 }}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl backdrop-blur-xl bg-white/90 border border-slate-200/80 hover:border-emerald-500/50 transition-colors text-[12px] font-medium text-slate-700"
                data-testid={`trust-badge-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                <CheckCircle className="h-3.5 w-3.5 text-emerald-600" />
                <span>{label}</span>
              </motion.span>
            ))}
          </motion.div>

          {/* Floating dashboard mockup */}
          <motion.div
            data-testid="hero-dashboard-preview"
            className="relative mt-16 lg:mt-20 max-w-6xl mx-auto"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
          >
            {/* Aurora halo behind the mockup */}
            <div aria-hidden className="absolute inset-x-0 -top-20 h-72 bg-gradient-to-b from-emerald-500/20 via-teal-400/10 to-transparent blur-3xl pointer-events-none" />
            <motion.div
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
              className="relative rounded-3xl overflow-hidden border border-slate-200 shadow-[0_40px_100px_-24px_rgba(15,23,42,0.18)] bg-white"
            >
              {/* Browser chrome */}
              <div className="flex items-center gap-1.5 px-4 h-9 bg-slate-100 border-b border-slate-200">
                <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
                <span className="ml-4 text-[10px] font-mono text-slate-500 truncate">
                  app.out-play.in/dashboard
                </span>
              </div>
              <img
                src="/screenshots/dashboard.jpg"
                alt="Out-Play dashboard preview — students, KPIs, autonomous billing"
                className="w-full block"
                loading="eager"
              />
              {/* Floating callouts */}
              <div className="hidden md:flex absolute top-14 right-6 items-center gap-1.5 px-3 py-1.5 rounded-full backdrop-blur-xl bg-emerald-600 text-white text-[11px] font-semibold shadow-[0_8px_24px_-4px_rgba(16,185,129,0.35)]">
                <Sparkles className="h-3 w-3" /> Auto Fee Generation live
              </div>
              <div className="hidden md:flex absolute bottom-6 left-6 items-center gap-1.5 px-3 py-1.5 rounded-full backdrop-blur-xl bg-violet-600 text-white text-[11px] font-semibold shadow-[0_8px_24px_-4px_rgba(139,92,246,0.35)]">
                <Zap className="h-3 w-3" /> WhatsApp Cloud API
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ===== METRICS — Phase 648 ===== */}
      <section id="metrics" data-testid="metrics-section" className="relative py-24 bg-[#FAFAFB] border-t border-slate-200/60">
        <div className="absolute inset-0 -z-10 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-64 bg-emerald-500/[0.14] blur-[100px] rounded-full" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Anim className="text-center mb-14">
            <p className="text-[11px] font-medium tracking-[0.2em] uppercase text-emerald-600 mb-3">By the numbers</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight">
              Powering the future of<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600">Indian sports academies.</span>
            </h2>
          </Anim>

          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 lg:gap-4">
            {[
              { value: 40, suffix: '+', label: 'Sports Supported' },
              { value: 1600, suffix: '+', label: 'Academies Managed' },
              { value: 2, suffix: 'M+', label: 'Sessions Managed' },
              { value: 100, prefix: '₹', suffix: ' Cr+', label: 'Fees Processed' },
              { value: 99.9, suffix: '%', label: 'Platform Uptime', decimals: 1 },
            ].map((m, i) => (
              <MetricCard
                key={m.label}
                value={m.value}
                prefix={m.prefix}
                suffix={m.suffix}
                label={m.label}
                decimals={m.decimals}
                delay={i}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Phase 648 — YouTube walkthrough modal */}
      <Dialog open={videoOpen} onOpenChange={setVideoOpen}>
        <DialogContent
          className="max-w-4xl p-0 bg-black border-slate-200/80 overflow-hidden"
          data-testid="hero-video-modal"
        >
          <DialogHeader className="sr-only">
            <DialogTitle>Out-Play 60-second Product Tour</DialogTitle>
          </DialogHeader>
          <div className="relative w-full aspect-video bg-black">
            {videoOpen && (
              <iframe
                src="https://www.youtube.com/embed/ABOfFEaShZU?autoplay=1&rel=0&modestbranding=1"
                title="Out-Play 60-second Product Tour"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="absolute inset-0 w-full h-full border-0"
                data-testid="hero-video-iframe"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>


      {/* ===== PHASE 1 · SOCIAL PROOF — sport-vertical trust bar ===== */}
      <section
        className="relative py-16 bg-[#FAFAFB] border-t border-slate-200/60"
        data-testid="social-proof-section"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Anim className="text-center mb-10">
            <p className="text-[11px] font-medium tracking-[0.22em] uppercase text-slate-500 mb-4">Trusted by</p>
            <h3 className="text-xl sm:text-2xl font-semibold text-slate-700">
              Academies across every sport, from Bengaluru to Kolkata.
            </h3>
          </Anim>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              { name: 'Champions FC', vertical: 'Football', color: 'from-emerald-400/80 to-emerald-300/80' },
              { name: 'Vijayawada Blues FC', vertical: 'Football', color: 'from-sky-400/80 to-cyan-300/80' },
              { name: 'THE FIRST AIM', vertical: 'Badminton', color: 'from-amber-400/80 to-orange-300/80' },
              { name: 'Bengaluru SA', vertical: 'Basketball', color: 'from-violet-400/80 to-fuchsia-300/80' },
              { name: 'Kolkata CC', vertical: 'Cricket', color: 'from-rose-400/80 to-pink-300/80' },
              { name: '+ 22 more', vertical: 'Growing every week', color: 'from-teal-400/80 to-emerald-300/80' },
            ].map((brand, i) => (
              <motion.div
                key={brand.name}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                whileHover={{ y: -2 }}
                className="group relative rounded-2xl backdrop-blur-xl bg-white border border-slate-200/70 hover:border-slate-300 transition-colors p-4 text-center overflow-hidden"
                data-testid={`social-proof-logo-${i}`}
              >
                <div className={`absolute -inset-8 bg-gradient-to-br ${brand.color} opacity-0 group-hover:opacity-[0.08] blur-2xl transition-opacity duration-500`} />
                <div className="relative">
                  <div className="text-sm font-semibold text-slate-800 group-hover:text-slate-900 transition-colors">{brand.name}</div>
                  <div className="text-[10px] font-medium text-slate-500 mt-1 tracking-[0.12em] uppercase">{brand.vertical}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== PHASE 1 · WHY OUT-PLAY — 3-column premium cards ===== */}
      <section
        className="relative py-24 bg-[#FAFAFB]"
        data-testid="why-outplay-section"
      >
        <div className="absolute inset-0 -z-10 pointer-events-none">
          <div className="absolute top-1/3 left-0 w-[500px] h-[500px] rounded-full bg-emerald-500/[0.14] blur-[140px]" />
          <div className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full bg-teal-400/[0.08] blur-[120px]" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Anim className="text-center mb-16 max-w-3xl mx-auto">
            <p className="text-[11px] font-medium tracking-[0.22em] uppercase text-emerald-600 mb-3">Why Out-Play</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight leading-[1.1]">
              Three shifts that turn an academy into<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600">a compounding business.</span>
            </h2>
          </Anim>
          <div className="grid md:grid-cols-3 gap-4 lg:gap-5">
            {[
              {
                icon: Zap,
                title: 'Run Operations',
                subtitle: 'Sessions · Attendance · Coach ops',
                copy: 'Auto-schedule sessions, track attendance with a tap, and give every coach a mobile-first workspace. What used to take 40 admin hours a week now takes 4.',
                accent: 'from-emerald-500/25 to-teal-500/10',
                border: 'group-hover:border-emerald-500/60',
              },
              {
                icon: Bot,
                title: 'Automate Administration',
                subtitle: 'Fees · WhatsApp · Reminders',
                copy: 'Fee Bills auto-generate on the 1st. Reminders fire on WhatsApp. Receipts land in inboxes. Chronic non-payers get suspended. Zero manual chase.',
                accent: 'from-violet-500/25 to-fuchsia-500/10',
                border: 'group-hover:border-violet-400/40',
              },
              {
                icon: TrendingUp,
                title: 'Grow Revenue',
                subtitle: 'CRM · AI Growth · Referrals',
                copy: 'A built-in CRM converts every enquiry, AI drafts your marketing every Monday, and parent referral loops keep your funnel full — while you focus on coaching.',
                accent: 'from-amber-500/25 to-orange-500/10',
                border: 'group-hover:border-amber-400/40',
              },
            ].map((card, i) => (
              <motion.div
                key={card.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
                whileHover={{ y: -6 }}
                className={`group relative rounded-3xl backdrop-blur-xl bg-white border border-slate-200/70 ${card.border} transition-colors p-8 lg:p-10 overflow-hidden`}
                data-testid={`why-card-${card.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className={`absolute -inset-8 bg-gradient-to-br ${card.accent} opacity-0 group-hover:opacity-100 blur-3xl transition-opacity duration-700 pointer-events-none`} />
                <div className="relative">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-slate-100/70 border border-slate-200/80 group-hover:border-slate-300 transition-colors mb-6">
                    <card.icon className="h-5 w-5 text-emerald-700" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-1 tracking-tight">{card.title}</h3>
                  <p className="text-[11px] font-medium tracking-[0.15em] uppercase text-slate-500 mb-4">{card.subtitle}</p>
                  <p className="text-[15px] leading-relaxed text-slate-600">{card.copy}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== PHASE 1 · FEATURE SHOWCASE — alternating module deep-dives ===== */}
      <section
        className="relative py-24 bg-[#FAFAFB]"
        data-testid="feature-showcase-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Anim className="text-center mb-16 max-w-3xl mx-auto">
            <p className="text-[11px] font-medium tracking-[0.22em] uppercase text-emerald-600 mb-3">Everything in one platform</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight leading-[1.1]">
              A dedicated module<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600">for every academy workflow.</span>
            </h2>
          </Anim>

          <div className="space-y-24 lg:space-y-32">
            {[
              {
                testid: 'showcase-academy-crm',
                eyebrow: 'Academy CRM',
                title: 'Turn enquiries into paying students.',
                copy: 'A purpose-built CRM tracks every parent enquiry through call → trial → paid, auto-nudges cold leads on WhatsApp, and shows you the ROI of every marketing rupee.',
                bullets: ['Lead pipeline · Trial → Paid conversion', 'Auto-nudge on WhatsApp', 'Source attribution', 'Parent contact history'],
                img: '/screenshots/students.jpg',
                icon: Users,
              },
              {
                testid: 'showcase-finance',
                eyebrow: 'Finance & Fees',
                title: 'Fee Bills that generate themselves.',
                copy: 'Auto-generated Fee Bills on the 1st, WhatsApp reminders on day 3, Razorpay UPI links auto-embedded. Payments reconcile automatically. 96% on-time collection observed.',
                bullets: ['Razorpay UPI · Cards · NetBanking', 'Auto WhatsApp reminders', 'Reconciliation on autopilot', 'Expense + revenue P&L'],
                img: '/screenshots/finance.jpg',
                icon: IndianRupee,
              },
              {
                testid: 'showcase-attendance',
                eyebrow: 'Attendance & Sessions',
                title: 'One-tap attendance from any device.',
                copy: 'Coaches mark attendance from their phone, parents get real-time WhatsApp alerts, and you get a live heatmap of every session — no more paper registers.',
                bullets: ['Kiosk / mobile / QR check-in', 'Live session heatmap', 'Auto-alert on absence streaks', 'Session plan integration'],
                img: '/screenshots/attendance.jpg',
                icon: ClipboardCheck,
              },
              {
                testid: 'showcase-hrms',
                eyebrow: 'Coach & Staff HRMS',
                title: 'Payroll, performance & scorecards.',
                copy: 'Track every coach\'s sessions, attendance, and performance ratings. Auto-generate monthly payslips based on hours delivered. Everything a modern academy HRM should be.',
                bullets: ['Payroll auto-runs on 30th', 'Performance scorecards', 'Session-hours tracking', 'Leave & shift management'],
                img: '/screenshots/hrms.jpg',
                icon: Briefcase,
              },
              {
                testid: 'showcase-tournaments',
                eyebrow: 'Tournament & Event Management',
                title: 'Host and broadcast every event.',
                copy: 'Draft brackets, publish schedules, print ID cards, and broadcast live scores to parents — all from one dashboard. Every academy is a tournament host in waiting.',
                bullets: ['Auto brackets · Round-robin · Knockout', 'Live scorecards', 'Parent broadcasts', 'Event ticketing (Razorpay)'],
                img: '/screenshots/events.jpg',
                icon: Trophy,
              },
              {
                testid: 'showcase-dashboard',
                eyebrow: 'Owner Dashboard',
                title: 'The command centre for your entire business.',
                copy: 'Every KPI you actually care about — revenue, attendance, coach load, retention — updated in real time. Drill into any metric with one click.',
                bullets: ['Revenue MoM · Retention · LTV', 'Cohort analytics', 'Every KPI drills down', 'Custom exports (Excel / PDF)'],
                img: '/screenshots/dashboard.jpg',
                icon: BarChart3,
              },
            ].map((mod, i) => {
              const flip = i % 2 === 1;
              return (
                <motion.div
                  key={mod.testid}
                  data-testid={mod.testid}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                  className={`grid lg:grid-cols-2 gap-10 lg:gap-16 items-center ${flip ? 'lg:[&>div:first-child]:order-2' : ''}`}
                >
                  {/* Copy side */}
                  <div>
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full backdrop-blur-md bg-slate-50 border border-slate-200/80 text-emerald-700 text-[11px] font-medium tracking-[0.15em] uppercase mb-5">
                      <mod.icon className="h-3.5 w-3.5" />
                      {mod.eyebrow}
                    </div>
                    <h3 className="text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight leading-[1.15] mb-5">
                      {mod.title}
                    </h3>
                    <p className="text-[15px] lg:text-base leading-relaxed text-slate-600 mb-7 max-w-xl">
                      {mod.copy}
                    </p>
                    <ul className="space-y-2.5">
                      {mod.bullets.map((b) => (
                        <li key={b} className="flex items-start gap-2.5 text-[14px] text-slate-700">
                          <span className="mt-1.5 flex-shrink-0 w-1.5 h-1.5 rounded-full bg-emerald-400" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  {/* Screenshot side */}
                  <motion.div
                    whileHover={{ y: -6 }}
                    transition={{ duration: 0.4 }}
                    className="relative"
                  >
                    <div aria-hidden className="absolute -inset-4 bg-gradient-to-br from-emerald-500/20 to-teal-500/10 blur-2xl opacity-40" />
                    <div className="relative rounded-2xl overflow-hidden border border-slate-200 shadow-[0_20px_60px_-16px_rgba(15,23,42,0.18)] bg-white">
                      <div className="flex items-center gap-1.5 px-4 h-8 bg-slate-100 border-b border-slate-200">
                        <span className="w-2 h-2 rounded-full bg-[#ff5f57]" />
                        <span className="w-2 h-2 rounded-full bg-[#febc2e]" />
                        <span className="w-2 h-2 rounded-full bg-[#28c840]" />
                      </div>
                      <img src={mod.img} alt={`${mod.eyebrow} module preview`} className="w-full block" loading="lazy" />
                    </div>
                  </motion.div>
                </motion.div>
              );
            })}
          </div>

          {/* Compact grid — remaining 5 modules teased below */}
          <div className="mt-24 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {[
              { icon: Radio, label: 'Broadcast Studio' },
              { icon: MapPin, label: 'Arena Booking' },
              { icon: Smartphone, label: 'Parent Portal' },
              { icon: Brain, label: 'AI Coach' },
              { icon: Video, label: 'Video Analysis' },
            ].map((f, i) => (
              <motion.div
                key={f.label}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                whileHover={{ y: -3 }}
                className="rounded-2xl backdrop-blur-xl bg-white border border-slate-200/70 hover:border-emerald-500/40 transition-colors p-5"
                data-testid={`compact-module-${f.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <f.icon className="h-5 w-5 text-emerald-700 mb-3" />
                <div className="text-sm font-semibold text-slate-900">{f.label}</div>
                <div className="text-[11px] text-slate-500 mt-1">Included in every plan</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== PHASE 1 · AI WORKFORCE — Meet Your Academy's AI Team ===== */}
      <section
        className="relative py-24 bg-gradient-to-b from-white via-[#F5FBF8] to-white overflow-hidden"
        data-testid="ai-workforce-section"
      >
        {/* Futuristic backdrop */}
        <div className="absolute inset-0 -z-10 pointer-events-none">
          <motion.div
            className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] rounded-full bg-gradient-to-br from-violet-200/60 via-emerald-200/50 to-cyan-200/50 blur-[120px]"
            animate={{ scale: [1, 1.08, 1], opacity: [0.6, 0.9, 0.6] }}
            transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
          />
          <div className="absolute inset-0 opacity-[0.06] bg-[linear-gradient(rgba(16,185,129,0.35)_1px,transparent_1px),linear-gradient(90deg,rgba(16,185,129,0.35)_1px,transparent_1px)] bg-[size:48px_48px]" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Anim className="text-center mb-16 max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full backdrop-blur-xl bg-violet-100/70 border border-violet-500/50 text-violet-700 text-[11px] font-medium tracking-[0.15em] uppercase mb-5">
              <Sparkles className="h-3 w-3" />
              New in 2026
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight leading-[1.1] mb-4">
              Meet your academy&rsquo;s<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-600 via-emerald-600 to-cyan-600">AI Workforce.</span>
            </h2>
            <p className="text-[15px] text-slate-600 leading-relaxed max-w-2xl mx-auto">
              Six specialised AI agents that never sleep, never quit, and cost less than a single hire. They run in the background while you focus on coaching.
            </p>
          </Anim>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              {
                icon: Brain, name: 'AI Coach',
                copy: 'Personalised drills, weekly training plans and progress feedback for every athlete.',
                gradient: 'from-violet-500/25 to-fuchsia-500/10',
              },
              {
                icon: Film, name: 'AI Video Analyst',
                copy: 'Break down match footage, annotate movements, and export coaching clips in seconds.',
                gradient: 'from-cyan-500/25 to-teal-500/10',
              },
              {
                icon: Award, name: 'AI Nutrition Assistant',
                copy: 'Sport-specific diet plans, hydration reminders, and weekly meal charts for every athlete.',
                gradient: 'from-lime-500/25 to-emerald-500/10',
              },
              {
                icon: Shield, name: 'AI Injury Assistant',
                copy: 'Load-monitoring, recovery protocols, and red-flag alerts before an injury becomes a season.',
                gradient: 'from-rose-500/25 to-red-500/10',
              },
              {
                icon: BarChart3, name: 'AI Attendance Insights',
                copy: 'Predicts drop-outs 2 weeks in advance and drafts the win-back message for you.',
                gradient: 'from-amber-500/25 to-orange-500/10',
              },
              {
                icon: IndianRupee, name: 'AI Fee Follow-up',
                copy: 'Personalised WhatsApp nudges for every unpaid parent — polite, effective, always on time.',
                gradient: 'from-emerald-500/25 to-teal-500/10',
              },
            ].map((agent, i) => (
              <motion.div
                key={agent.name}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.07, ease: [0.16, 1, 0.3, 1] }}
                whileHover={{ y: -6 }}
                className="group relative rounded-3xl backdrop-blur-xl bg-white border border-slate-200/70 hover:border-violet-500/50 transition-colors p-6 overflow-hidden"
                data-testid={`ai-agent-${agent.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className={`absolute -inset-10 bg-gradient-to-br ${agent.gradient} opacity-0 group-hover:opacity-100 blur-3xl transition-opacity duration-700 pointer-events-none`} />
                <div className="relative">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex-shrink-0 inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-gradient-to-br from-emerald-50 to-white border border-slate-200 group-hover:border-emerald-500/50 transition-colors">
                      <agent.icon className="h-5 w-5 text-emerald-700" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-base font-semibold text-slate-900">{agent.name}</h3>
                      <p className="text-[10px] font-medium tracking-[0.14em] uppercase text-emerald-600/70">Autonomous · always-on</p>
                    </div>
                  </div>
                  <p className="text-[14px] leading-relaxed text-slate-600">{agent.copy}</p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Explore CTA */}
          <div className="mt-14 text-center">
            <button
              type="button"
              onClick={() => setAiSuiteOpen(true)}
              data-testid="ai-workforce-explore-cta"
              className="group inline-flex items-center gap-2 px-6 py-3 rounded-2xl backdrop-blur-xl bg-slate-100/70 hover:bg-slate-100 border border-slate-200 hover:border-emerald-500/50 text-slate-900 font-medium text-sm transition-colors"
            >
              Explore the full AI Suite
              <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
            </button>
          </div>
        </div>
      </section>


      {/* Phase 282e — Star Academy social proof (real customer wins) */}
      <div className="bg-[#FAFAFB] pt-4">
        <StarAcademyProofStrip />
      </div>

      {/* ===== PHASE 2 · STREAMX / LIVE — Premium broadcast section ===== */}
      <section
        className="relative py-24 lg:py-32 bg-gradient-to-b from-white via-[#FEF7F5] to-white overflow-hidden"
        data-testid="livex-premium-section"
      >
        <div aria-hidden className="absolute inset-0 -z-10 pointer-events-none">
          <motion.div
            className="absolute top-0 left-1/3 -translate-x-1/2 w-[900px] h-[600px] rounded-full bg-rose-500/[0.10] blur-[140px]"
            animate={{ opacity: [0.6, 1, 0.6] }}
            transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
          />
          <div className="absolute bottom-0 right-1/4 w-[700px] h-[500px] rounded-full bg-amber-500/[0.10] blur-[120px]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-16 items-center">
            {/* Copy side */}
            <div>
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full backdrop-blur-xl bg-rose-100/70 border border-rose-500/50 text-rose-600 text-[11px] font-medium tracking-[0.18em] uppercase mb-6"
              >
                <span className="relative flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-rose-400" />
                </span>
                StreamX · Live now
              </motion.div>

              <motion.h2
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.65, delay: 0.08, ease: [0.16, 1, 0.3, 1] }}
                className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight leading-[1.05]"
                data-testid="livex-premium-headline"
              >
                Broadcast every match.<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-rose-600 via-orange-600 to-amber-600">
                  Turn every parent into a fan.
                </span>
              </motion.h2>

              <motion.p
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="mt-6 text-[15px] lg:text-base leading-relaxed text-slate-600 max-w-xl"
              >
                Go live from a single phone in under 60 seconds. Mark highlights with a tap. AI auto-chapters replays. Parents get every moment on WhatsApp — before the match is over.
              </motion.p>

              <ul className="mt-8 space-y-3 max-w-lg">
                {[
                  { icon: Zap, text: 'Go live in under 60 seconds — one phone, one tap' },
                  { icon: Wand2, text: 'AI-highlights: goals, wickets, aces auto-detected' },
                  { icon: Film, text: '90-day replay vault with chapter markers' },
                  { icon: MessageCircle, text: 'Live parent chat + reactions overlay' },
                ].map((row) => (
                  <motion.li
                    key={row.text}
                    initial={{ opacity: 0, x: -8 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4 }}
                    className="flex items-start gap-3 text-[14px] text-slate-700"
                  >
                    <span className="mt-0.5 inline-flex items-center justify-center w-7 h-7 rounded-lg bg-rose-100/70 border border-rose-500/40 flex-shrink-0">
                      <row.icon className="h-3.5 w-3.5 text-rose-600" />
                    </span>
                    <span>{row.text}</span>
                  </motion.li>
                ))}
              </ul>

              <div className="mt-10 flex flex-col sm:flex-row gap-3">
                <Link to="/streamx" data-testid="livex-premium-cta-explore" className="w-full sm:w-auto">
                  <motion.span
                    whileHover={{ y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    className="group inline-flex w-full sm:w-auto items-center justify-center gap-2 h-12 px-6 rounded-2xl font-semibold text-[14px] text-slate-900 bg-gradient-to-r from-rose-500 to-orange-500 hover:from-rose-400 hover:to-orange-400 shadow-[0_12px_30px_-8px_rgba(244,63,94,0.35)] transition-all duration-300"
                  >
                    <Radio className="h-4 w-4" />
                    Explore StreamX
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                  </motion.span>
                </Link>
                <Link to="/streamx/live" data-testid="livex-premium-cta-watch" className="w-full sm:w-auto">
                  <motion.span
                    whileHover={{ y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    className="inline-flex w-full sm:w-auto items-center justify-center gap-2 h-12 px-6 rounded-2xl font-semibold text-[14px] text-slate-900 backdrop-blur-xl bg-slate-100/70 hover:bg-slate-100 border border-slate-200 hover:border-slate-300 transition-colors"
                  >
                    <Play className="h-4 w-4 fill-white" />
                    Watch a live match
                  </motion.span>
                </Link>
              </div>
            </div>

            {/* Live preview mockup */}
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="relative"
              data-testid="livex-premium-preview"
            >
              <div aria-hidden className="absolute -inset-6 bg-gradient-to-br from-rose-500/25 via-orange-500/15 to-transparent blur-3xl opacity-70" />
              <motion.div
                animate={{ y: [0, -6, 0] }}
                transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
                className="relative rounded-3xl overflow-hidden border border-slate-900 shadow-[0_30px_80px_-24px_rgba(244,63,94,0.28)] bg-black"
              >
                {/* Simulated broadcast viewer */}
                <div className="relative aspect-[16/10] bg-gradient-to-br from-emerald-950 via-emerald-900 to-teal-950 overflow-hidden">
                  {/* Field lines */}
                  <div aria-hidden className="absolute inset-0 opacity-30">
                    <div className="absolute inset-x-0 top-1/2 h-px bg-white/40" />
                    <div className="absolute inset-y-0 left-1/2 w-px bg-white/40" />
                    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 rounded-full border border-white/40" />
                  </div>

                  {/* Live pill */}
                  <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-rose-500 text-slate-900 text-[11px] font-bold uppercase tracking-widest shadow-lg">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
                    </span>
                    Live
                  </div>

                  {/* Viewer count */}
                  <div className="absolute top-4 right-4 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-black/60 backdrop-blur-md border border-white/20 text-white text-[11px] font-semibold">
                    <Eye className="h-3 w-3" />
                    <span className="tabular-nums">1,247</span>
                    <span className="text-white/60">watching</span>
                  </div>

                  {/* Score bug */}
                  <div className="absolute bottom-4 left-4 flex items-center gap-3 px-4 py-2.5 rounded-xl bg-black/70 backdrop-blur-xl border border-white/15 shadow-2xl">
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-emerald-500 text-[10px] font-black flex items-center justify-center text-slate-900">CF</span>
                      <span className="text-white text-sm font-semibold">Champions FC</span>
                    </div>
                    <span className="text-emerald-600 text-lg font-black tabular-nums">2</span>
                    <span className="text-white/40 text-xs">-</span>
                    <span className="text-white text-lg font-black tabular-nums">1</span>
                    <div className="flex items-center gap-2">
                      <span className="text-white text-sm font-semibold">Blues SA</span>
                      <span className="w-5 h-5 rounded-full bg-sky-500 text-[10px] font-black flex items-center justify-center text-slate-900">BS</span>
                    </div>
                    <span className="text-[10px] text-rose-600 font-bold ml-2">67&apos;</span>
                  </div>

                  {/* Chat overlay */}
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.35 }}
                    className="absolute bottom-4 right-4 w-56 space-y-1.5"
                  >
                    {[
                      { name: 'Aarav\u2019s Dad', msg: 'What a save! 🧤', tint: 'text-emerald-700' },
                      { name: 'Coach K', msg: 'Great press by the U-15s', tint: 'text-cyan-700' },
                      { name: 'Priya', msg: 'GOAL! 🎯🎯🎯', tint: 'text-amber-600' },
                    ].map((m, idx) => (
                      <motion.div
                        key={m.name}
                        initial={{ opacity: 0, x: 20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.5 + idx * 0.15 }}
                        className="px-3 py-1.5 rounded-lg bg-black/60 backdrop-blur-md border border-white/15 text-[11px]"
                      >
                        <span className={`font-semibold ${m.tint}`}>{m.name}</span>
                        <span className="text-white/80 ml-1.5">{m.msg}</span>
                      </motion.div>
                    ))}
                  </motion.div>

                  {/* Signal indicator */}
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 hidden md:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-100 backdrop-blur-md border border-emerald-500/50 text-emerald-700 text-[10px] font-semibold uppercase tracking-widest">
                    <Signal className="h-3 w-3" />
                    HD · 1080p
                  </div>
                </div>

                {/* Stats strip below the player */}
                <div className="grid grid-cols-3 divide-x divide-slate-800/60 bg-slate-950 border-t border-slate-800">
                  {[
                    { l: 'Goes live in', v: '< 60s', tint: 'text-rose-600' },
                    { l: 'Replay vault', v: '90 days', tint: 'text-emerald-700' },
                    { l: 'AI highlights', v: '6 types', tint: 'text-amber-600' },
                  ].map((s) => (
                    <div key={s.l} className="p-4 text-center">
                      <div className={`text-lg font-bold tabular-nums ${s.tint}`}>{s.v}</div>
                      <div className="text-[10px] font-medium text-slate-500 uppercase tracking-[0.12em] mt-0.5">{s.l}</div>
                    </div>
                  ))}
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== PHASE 2 · ARENAX BOOKINGS — Premium ===== */}
      <section
        className="relative py-24 lg:py-32 bg-[#FAFAFB] overflow-hidden"
        data-testid="arenax-premium-section"
      >
        <div aria-hidden className="absolute inset-0 -z-10 pointer-events-none">
          <div className="absolute top-0 right-1/4 w-[700px] h-[500px] rounded-full bg-cyan-500/[0.12] blur-[130px]" />
          <div className="absolute bottom-0 left-1/4 w-[700px] h-[500px] rounded-full bg-amber-500/[0.10] blur-[120px]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[1fr_1.05fr] gap-12 lg:gap-16 items-center">
            {/* ArenaX preview mockup */}
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="relative order-2 lg:order-1"
              data-testid="arenax-premium-preview"
            >
              <div aria-hidden className="absolute -inset-6 bg-gradient-to-br from-cyan-500/25 via-amber-500/15 to-transparent blur-3xl opacity-70" />

              {/* Booking calendar */}
              <motion.div
                animate={{ y: [0, -6, 0] }}
                transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
                className="relative rounded-3xl overflow-hidden border border-slate-200 shadow-[0_30px_80px_-24px_rgba(8,145,178,0.20)] bg-white"
              >
                <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-slate-200/60">
                  <div>
                    <div className="text-[10px] font-medium text-slate-500 uppercase tracking-[0.15em]">Turf · Ground 1</div>
                    <div className="text-slate-900 font-semibold text-sm mt-0.5">Bookings — Tue, Feb 25</div>
                  </div>
                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-emerald-100/70 border border-emerald-500/40 text-emerald-700 text-[10px] font-semibold">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                    Live
                  </div>
                </div>

                {/* Time slots grid */}
                <div className="grid grid-cols-4 gap-2 p-5">
                  {[
                    { t: '06:00', s: 'booked', who: 'CF U-15' },
                    { t: '07:00', s: 'booked', who: 'Aditya B.' },
                    { t: '08:00', s: 'open' },
                    { t: '09:00', s: 'booked', who: 'Blues Ac.' },
                    { t: '10:00', s: 'open' },
                    { t: '11:00', s: 'booked', who: 'Corporate' },
                    { t: '16:00', s: 'booked', who: 'Rohit F.' },
                    { t: '17:00', s: 'peak', who: 'Peak · ₹₹' },
                    { t: '18:00', s: 'booked', who: 'CF Seniors' },
                    { t: '19:00', s: 'peak', who: 'Peak · ₹₹' },
                    { t: '20:00', s: 'booked', who: 'League Div-B' },
                    { t: '21:00', s: 'open' },
                  ].map((slot, idx) => {
                    const tone = slot.s === 'booked'
                      ? 'bg-cyan-100/80 border-cyan-500/50 text-cyan-800'
                      : slot.s === 'peak'
                      ? 'bg-amber-100/70 border-amber-500/50 text-amber-800'
                      : 'bg-white border-slate-200/80 text-slate-600';
                    return (
                      <motion.div
                        key={`${slot.t}-${idx}`}
                        initial={{ opacity: 0, y: 8 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.35, delay: idx * 0.03 }}
                        className={`rounded-xl border p-2.5 ${tone}`}
                      >
                        <div className="text-[10px] font-semibold tabular-nums">{slot.t}</div>
                        <div className="text-[10px] mt-1 truncate opacity-80">{slot.who || 'Open'}</div>
                      </motion.div>
                    );
                  })}
                </div>

                {/* Revenue strip */}
                <div className="grid grid-cols-3 divide-x divide-slate-200 bg-slate-50/70 border-t border-slate-200">
                  {[
                    { l: 'Today', v: '\u20b924.6k', tint: 'text-emerald-700' },
                    { l: 'Week', v: '\u20b91.42L', tint: 'text-cyan-700' },
                    { l: 'Utilisation', v: '78%', tint: 'text-amber-600' },
                  ].map((s) => (
                    <div key={s.l} className="p-4 text-center">
                      <div className={`text-lg font-bold tabular-nums ${s.tint}`}>{s.v}</div>
                      <div className="text-[10px] font-medium text-slate-500 uppercase tracking-[0.12em] mt-0.5">{s.l}</div>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Floating commission chip */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="hidden md:flex absolute -bottom-6 -right-4 items-center gap-2 px-4 py-2.5 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 text-white font-bold text-sm shadow-[0_10px_30px_-6px_rgba(16,185,129,0.5)]"
                data-testid="arenax-premium-commission-chip"
              >
                <Sparkles className="h-4 w-4" />
                Legend Tier: 0% commission
              </motion.div>
            </motion.div>

            {/* Copy side */}
            <div className="order-1 lg:order-2">
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full backdrop-blur-xl bg-cyan-100/70 border border-cyan-500/50 text-cyan-700 text-[11px] font-medium tracking-[0.18em] uppercase mb-6"
              >
                <Sparkles className="h-3 w-3" />
                ArenaX · Bookings + Tournaments
              </motion.div>

              <motion.h2
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.65, delay: 0.08, ease: [0.16, 1, 0.3, 1] }}
                className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight leading-[1.05]"
                data-testid="arenax-premium-headline"
              >
                Fill every slot.<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 via-teal-600 to-amber-600">
                  Keep every rupee.
                </span>
              </motion.h2>

              <motion.p
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="mt-6 text-[15px] lg:text-base leading-relaxed text-slate-600 max-w-xl"
              >
                Turf bookings, league draws, knockout brackets, and WhatsApp fixture blasts — at an
                industry-leading <span className="text-amber-600 font-semibold">3.5% flat marketplace fee</span> for independent hosts.
                Legend Tier academies pay <span className="text-emerald-700 font-semibold">0% commission, forever.</span>
              </motion.p>

              <ul className="mt-8 space-y-3 max-w-lg">
                {[
                  { icon: CalendarDays, text: 'Real-time booking calendar · UPI payments in seconds' },
                  { icon: Trophy, text: 'Auto brackets · Round-robin · Knockout draws' },
                  { icon: Send, text: 'Instant WhatsApp fixture blasts to parents & teams' },
                  { icon: BarChart3, text: 'Revenue analytics · utilisation heatmaps · peak pricing' },
                ].map((row) => (
                  <motion.li
                    key={row.text}
                    initial={{ opacity: 0, x: -8 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4 }}
                    className="flex items-start gap-3 text-[14px] text-slate-700"
                  >
                    <span className="mt-0.5 inline-flex items-center justify-center w-7 h-7 rounded-lg bg-cyan-100/70 border border-cyan-500/40 flex-shrink-0">
                      <row.icon className="h-3.5 w-3.5 text-cyan-700" />
                    </span>
                    <span>{row.text}</span>
                  </motion.li>
                ))}
              </ul>

              <div className="mt-10 flex flex-col sm:flex-row gap-3">
                <Link to="/arenax" data-testid="arenax-premium-cta-explore" className="w-full sm:w-auto">
                  <motion.span
                    whileHover={{ y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    className="group inline-flex w-full sm:w-auto items-center justify-center gap-2 h-12 px-6 rounded-2xl font-semibold text-[14px] text-slate-900 bg-gradient-to-r from-cyan-500 to-amber-500 hover:from-cyan-400 hover:to-amber-400 shadow-[0_12px_30px_-8px_rgba(8,145,178,0.35)] transition-all duration-300"
                  >
                    <Sparkles className="h-4 w-4" />
                    Explore ArenaX
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                  </motion.span>
                </Link>
                <a href="#contact" data-testid="arenax-premium-cta-sales" className="w-full sm:w-auto">
                  <motion.span
                    whileHover={{ y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    className="inline-flex w-full sm:w-auto items-center justify-center gap-2 h-12 px-6 rounded-2xl font-semibold text-[14px] text-slate-900 backdrop-blur-xl bg-slate-100/70 hover:bg-slate-100 border border-slate-200 hover:border-slate-300 transition-colors"
                  >
                    <Mail className="h-4 w-4" />
                    Talk to Sales
                  </motion.span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== PHASE 112B — PERSONA SHOWCASE WIREFRAMES (light-mode refresh) ===== */}
      <section id="personas" className="relative py-24 lg:py-32 bg-[#FAFAFB] border-t border-slate-200/60 overflow-hidden">
        <div aria-hidden className="absolute inset-0 -z-10 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-[600px] h-[400px] rounded-full bg-emerald-500/[0.08] blur-[130px]" />
          <div className="absolute bottom-0 right-1/4 w-[500px] h-[400px] rounded-full bg-violet-500/[0.06] blur-[120px]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Anim className="text-center mb-14 max-w-3xl mx-auto">
            <p className="text-[11px] font-medium tracking-[0.22em] uppercase text-emerald-600 mb-3">Built for every role</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight leading-[1.1]">
              One platform.<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600">Three workspaces.</span>
            </h2>
            <p className="text-[15px] text-slate-600 leading-relaxed mt-4">
              The exact UI an Academy Owner, a Manager, and a Parent each see &mdash; same data, role-shaped surfaces.
            </p>
          </Anim>

          <div className="grid lg:grid-cols-3 gap-4 lg:gap-5">
            {/* Frame 1 — Academy Admin / Owner Workspace (Phase 140) */}
            <Anim delay={0.1}>
              <Card className="h-full rounded-3xl border border-slate-200 shadow-[0_20px_60px_-24px_rgba(244,63,94,0.18)] hover:shadow-[0_30px_80px_-24px_rgba(244,63,94,0.25)] hover:border-rose-500/40 transition-all overflow-hidden bg-white" data-testid="persona-card-academy-owner">
                <div className="p-5 border-b border-slate-200/70 bg-gradient-to-br from-rose-50 to-white">
                  <div className="flex items-start gap-3">
                    <span className="flex-shrink-0 inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-white border border-rose-200 shadow-sm">
                      <Briefcase className="h-5 w-5 text-rose-600" />
                    </span>
                    <div className="min-w-0">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 text-[10px] font-black uppercase tracking-widest border border-rose-200 mb-1.5">Vikram · Academy Owner</span>
                      <h3 className="font-bold text-lg text-slate-900 leading-tight">Academy Admin Workspace</h3>
                      <p className="text-[11px] text-slate-500 mt-0.5">Staff HRMS · Auto Payroll · Parent Invoicing · Lead Intel</p>
                    </div>
                  </div>
                </div>
                <CardContent className="p-5 space-y-3 bg-slate-50/40">
                  {/* Staff payroll snapshot */}
                  <div className="rounded-lg border border-rose-200 bg-white p-3" data-testid="persona-mock-payroll">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] font-semibold text-rose-700 flex items-center gap-1"><Briefcase className="h-3 w-3" /> May payroll · auto-run</span>
                      <span className="text-[10px] text-rose-700 font-bold">8 staff · ₹3.2L</span>
                    </div>
                    <div className="space-y-1.5">
                      {[
                        { name: 'Coach Arjun', role: 'Head Coach',   amt: '₹62,000', tone: 'bg-emerald-100 text-emerald-800' },
                        { name: 'Coach Meera', role: 'Asst Coach',   amt: '₹38,500', tone: 'bg-emerald-100 text-emerald-800' },
                        { name: 'Admin Kavya', role: 'Front-desk',   amt: '₹28,200', tone: 'bg-blue-100 text-blue-800' },
                      ].map((r) => (
                        <div key={r.name} className="flex items-center justify-between text-[10px]">
                          <span className="font-semibold truncate flex-1">{r.name}</span>
                          <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium ${r.tone}`}>{r.role}</span>
                          <span className="text-rose-700 font-bold ml-2 tabular-nums">{r.amt}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Lead Intel snapshot */}
                  <div className="rounded-lg border border-slate-200 bg-white p-3">
                    <p className="text-[10px] font-semibold text-slate-600 mb-2 flex items-center gap-1"><Target className="h-3 w-3" /> Lead Intel · this week</p>
                    <div className="space-y-1">
                      {[
                        { stage: 'New leads',    pct: 100, count: 24, color: 'bg-violet-500' },
                        { stage: 'Trial booked', pct: 58,  count: 14, color: 'bg-sky-500' },
                        { stage: 'Demo done',    pct: 38,  count: 9,  color: 'bg-amber-500' },
                        { stage: 'Enrolled',     pct: 21,  count: 5,  color: 'bg-emerald-500' },
                      ].map((s) => (
                        <div key={s.stage} className="flex items-center gap-2 text-[10px]">
                          <span className="w-16 truncate text-slate-600">{s.stage}</span>
                          <div className="flex-1 h-2 rounded bg-slate-100 overflow-hidden">
                            <div className={`h-full ${s.color} rounded`} style={{ width: `${s.pct}%` }} />
                          </div>
                          <span className="w-10 text-right text-slate-700 font-semibold tabular-nums">{s.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <p className="text-[10px] text-slate-500 italic flex items-center gap-1"><Bot className="h-3 w-3" /> Parent invoices auto-fire on day 1 · WhatsApp + email + UPI link</p>
                </CardContent>
              </Card>
            </Anim>

            {/* Frame 2 — Saritha's Manager/Employee view */}
            <Anim delay={0.2}>
              <Card className="h-full rounded-3xl border border-slate-200 shadow-[0_20px_60px_-24px_rgba(16,185,129,0.18)] hover:shadow-[0_30px_80px_-24px_rgba(16,185,129,0.25)] hover:border-emerald-500/40 transition-all overflow-hidden bg-white" data-testid="persona-card-manager">
                <div className="p-5 border-b border-slate-200/70 bg-gradient-to-br from-emerald-50 to-white">
                  <div className="flex items-start gap-3">
                    <span className="flex-shrink-0 inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-white border border-emerald-200 shadow-sm">
                      <MapPin className="h-5 w-5 text-emerald-600" />
                    </span>
                    <div className="min-w-0">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-black uppercase tracking-widest border border-emerald-200 mb-1.5">Saritha · Manager / Employee</span>
                      <h3 className="font-bold text-lg text-slate-900 leading-tight">Operational Control Deck</h3>
                      <p className="text-[11px] text-slate-500 mt-0.5">Centers · Batches · HRMS · Permissions</p>
                    </div>
                  </div>
                </div>
                <CardContent className="p-5 space-y-3 bg-slate-50/40">
                  {/* Centers grid */}
                  <div className="rounded-lg border border-emerald-200 bg-white p-3">
                    <p className="text-[10px] font-semibold text-emerald-700 mb-2 flex items-center gap-1"><MapPin className="h-3 w-3" /> Multi-Center Operations</p>
                    <div className="grid grid-cols-2 gap-1.5">
                      {[
                        { name: 'Vijayawada HQ',   students: 142, batches: 6 },
                        { name: 'Guntur Branch',   students: 88,  batches: 4 },
                        { name: 'Krishna Centre',  students: 67,  batches: 3 },
                        { name: 'Tenali Annex',    students: 41,  batches: 2 },
                      ].map((c) => (
                        <div key={c.name} className="border border-slate-200 rounded p-1.5 bg-slate-50">
                          <p className="text-[10px] font-semibold truncate">{c.name}</p>
                          <p className="text-[9px] text-slate-500">{c.students} students · {c.batches} batches</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Permissions matrix */}
                  <div className="rounded-lg border border-slate-200 bg-white p-3">
                    <p className="text-[10px] font-semibold text-slate-600 mb-2 flex items-center gap-1"><Shield className="h-3 w-3" /> HRMS Permissions</p>
                    <div className="space-y-1">
                      {[
                        { role: 'Saritha (Manager)', perms: ['Finance', 'CRM', 'Broadcasts'] },
                        { role: 'Ravi (Front-desk)', perms: ['Attendance', 'Fees'] },
                        { role: 'Akash (Coach)',     perms: ['Drills', 'Progress'] },
                      ].map((p) => (
                        <div key={p.role} className="flex items-center gap-2 text-[10px]">
                          <span className="w-24 truncate font-semibold">{p.role}</span>
                          <div className="flex flex-wrap gap-0.5 flex-1">
                            {p.perms.map((perm) => (
                              <span key={perm} className="px-1 py-0.5 rounded text-[9px] bg-emerald-100 text-emerald-800">{perm}</span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <p className="text-[10px] text-slate-500 italic flex items-center gap-1"><CheckCircle className="h-3 w-3" /> Auto-fee dispatch · Twilio WhatsApp templates</p>
                </CardContent>
              </Card>
            </Anim>

            {/* Frame 3 — Kiaansh's Parent view (multi-child) */}
            <Anim delay={0.3}>
              <Card className="h-full rounded-3xl border border-slate-200 shadow-[0_20px_60px_-24px_rgba(139,92,246,0.18)] hover:shadow-[0_30px_80px_-24px_rgba(139,92,246,0.25)] hover:border-violet-500/40 transition-all overflow-hidden bg-white" data-testid="persona-card-parent">
                <div className="p-5 border-b border-slate-200/70 bg-gradient-to-br from-violet-50 to-white">
                  <div className="flex items-start gap-3">
                    <span className="flex-shrink-0 inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-white border border-violet-200 shadow-sm">
                      <Smartphone className="h-5 w-5 text-violet-600" />
                    </span>
                    <div className="min-w-0">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 text-[10px] font-black uppercase tracking-widest border border-violet-200 mb-1.5">Kiaansh&apos;s Parent · Multi-Child</span>
                      <h3 className="font-bold text-lg text-slate-900 leading-tight">Unified Family Dashboard</h3>
                      <p className="text-[11px] text-slate-500 mt-0.5">One login · Two athletes · Zero collisions</p>
                    </div>
                  </div>
                </div>
                <CardContent className="p-5 space-y-3 bg-slate-50/40">
                  {/* Child selector pill */}
                  <div className="flex items-center justify-between bg-white border border-violet-200 rounded-lg px-3 py-1.5">
                    <span className="text-[10px] text-slate-500">Viewing</span>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-100 text-violet-800 text-[10px] font-semibold">
                      Kalyan Guda <ChevronDown className="h-3 w-3" />
                    </span>
                  </div>
                  {/* Per-child quick cards */}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="border border-violet-200 rounded-lg p-2 bg-white">
                      <p className="text-[10px] font-bold flex items-center gap-1">🏑 Kalyan</p>
                      <p className="text-[9px] text-slate-500 mt-0.5">Hockey · U-15</p>
                      <p className="text-[10px] text-emerald-700 mt-1.5 font-semibold">94% attendance</p>
                      <p className="text-[10px] text-emerald-700">Fee: Paid ✓</p>
                    </div>
                    <div className="border border-fuchsia-200 rounded-lg p-2 bg-white">
                      <p className="text-[10px] font-bold flex items-center gap-1">⚽ Kiaansh</p>
                      <p className="text-[9px] text-slate-500 mt-0.5">Football · U-11</p>
                      <p className="text-[10px] text-amber-700 mt-1.5 font-semibold">88% attendance</p>
                      <p className="text-[10px] text-rose-700">Fee: ₹1.5K pending ⚠️</p>
                    </div>
                  </div>
                  {/* Posture pill */}
                  <div className="flex items-center justify-between bg-white border border-slate-200 rounded-lg px-3 py-1.5">
                    <span className="text-[10px] text-slate-500 flex items-center gap-1">
                      <RefreshCw className="h-3 w-3" /> Identity
                    </span>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200 text-[10px] font-semibold">
                      <span className="relative flex h-1.5 w-1.5"><span className="absolute h-full w-full rounded-full bg-emerald-400 opacity-60 animate-ping" /><span className="relative h-1.5 w-1.5 rounded-full bg-emerald-500" /></span>
                      Parent · 2 roles
                    </span>
                  </div>
                  <p className="text-[10px] text-slate-500 italic flex items-center gap-1"><Sparkles className="h-3 w-3" /> Switch child OR switch role — never log out</p>
                </CardContent>
              </Card>
            </Anim>
          </div>
        </div>
      </section>

      {/* ===== PHASE 2 · PRICING — Premium dark ===== */}
      <section
        id="pricing"
        className="relative py-24 lg:py-32 bg-[#FAFAFB] overflow-hidden"
        data-testid="pricing-section"
      >
        <div aria-hidden className="absolute inset-0 -z-10 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] rounded-full bg-emerald-500/[0.10] blur-[140px]" />
          <div className="absolute bottom-0 right-1/4 w-[500px] h-[400px] rounded-full bg-violet-500/[0.08] blur-[120px]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Anim className="text-center mb-14 max-w-3xl mx-auto">
            <p className="text-[11px] font-medium tracking-[0.22em] uppercase text-emerald-600 mb-3">Simple, transparent pricing</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight leading-[1.1] mb-4">
              Start free.<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600">
                Scale when you&rsquo;re ready.
              </span>
            </h2>
            <p className="text-[15px] text-slate-600 leading-relaxed">
              14 days of full Pro access, no card required. Upgrade only when your academy is compounding.
            </p>
          </Anim>

          {/* Monthly / Annual toggle */}
          <div className="flex justify-center mb-10">
            <div className="inline-flex items-center gap-1 p-1 rounded-2xl backdrop-blur-xl bg-slate-50 border border-slate-200/80">
              <button
                type="button"
                onClick={() => setAnnualBilling(false)}
                data-testid="pricing-toggle-monthly"
                className={`px-4 py-2 rounded-xl text-[12px] font-semibold transition-colors ${!annualBilling ? 'bg-emerald-400 text-white shadow-[0_6px_16px_-4px_rgba(16,185,129,0.4)]' : 'text-slate-600 hover:text-slate-900'}`}
              >
                Monthly
              </button>
              <button
                type="button"
                onClick={() => setAnnualBilling(true)}
                data-testid="pricing-toggle-annual"
                className={`px-4 py-2 rounded-xl text-[12px] font-semibold transition-colors inline-flex items-center gap-1.5 ${annualBilling ? 'bg-emerald-400 text-white shadow-[0_6px_16px_-4px_rgba(16,185,129,0.4)]' : 'text-slate-600 hover:text-slate-900'}`}
              >
                Annual
                <span className={`text-[9px] font-black tracking-widest px-1.5 py-0.5 rounded ${annualBilling ? 'bg-emerald-950/20 text-white' : 'bg-emerald-400/20 text-emerald-700'}`}>SAVE 20%</span>
              </button>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4 lg:gap-5 max-w-5xl mx-auto">
            {[
              {
                name: 'Starter',
                slug: 'starter',
                badge: '\u23f3 Full access for 14 Days',
                priceMonthly: 0,
                priceAnnual: 0,
                period: '14 days free',
                desc: 'Full Pro Suite \u2014 zero card required',
                features: [
                  'Multi-Center Academy Operations',
                  'Auto Fee Management & Twilio WhatsApp Follow-ups',
                  'Attendance Sheets + Progress Grading Cards',
                  'HRMS \u00b7 Payroll \u00b7 Permissions',
                  'AI Inbound CRM + Public Intake Bot',
                  'Email support during trial',
                ],
                cta: 'Start Free Trial',
              },
              {
                name: 'Pro',
                slug: 'pro',
                priceMonthly: 999,
                priceAnnual: 799,
                period: '/month',
                desc: 'For active growing academies',
                features: [
                  'Multi-Center Academy Operations',
                  'Auto Fee Management & Twilio Follow-ups',
                  'Attendance Sheets + Progress Cards',
                  'Full HRMS \u00b7 Payroll \u00b7 Events',
                  'AI Drill Recommendation Engine',
                  'Priority WhatsApp + Email support',
                ],
                cta: 'Get Started',
                popular: true,
              },
              {
                name: 'Legend',
                slug: 'legend',
                priceMonthly: 1998,
                priceAnnual: 1598,
                period: '/month',
                desc: 'Pro + ArenaX + StreamX + Video Analysis \u2014 one bundle',
                features: [
                  'Everything in Pro',
                  { icon: 'Trophy', text: 'ArenaX bookings \u00b7 0% commission (vs 3.5% otherwise)' },
                  { icon: 'Video', text: 'StreamX broadcasting + replay vault (90-day retention)' },
                  { icon: 'Target', text: 'Video Analysis \u00b7 drill tagging \u00b7 player highlights' },
                  { icon: 'Bot', text: '8 AM AI Scraper CRM + Lead Intel auto-pipeline' },
                  { kind: 'ai-suite-divider' },
                  { icon: 'Share2', text: 'SocialX Omnichannel \u2014 1-click AI social scheduling', bold: true, chip: 'socialx' },
                  { icon: 'Sparkles', text: 'StudioX \u2014 Upscale footage to pristine 8K', bold: true, chip: 'studiox' },
                  { icon: 'Shield', text: 'Dedicated Account Manager \u00b7 SLA \u00b7 Federation licensing' },
                ],
                cta: 'Coming Soon',
                comingSoon: true,
              },
            ].map((p, i) => {
              const price = annualBilling ? p.priceAnnual : p.priceMonthly;
              return (
                <motion.div
                  key={p.name}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.55, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                  whileHover={{ y: -6 }}
                  className={`relative rounded-3xl backdrop-blur-xl p-7 lg:p-8 flex flex-col ${p.popular ? 'bg-gradient-to-b from-emerald-50 to-white border-2 border-emerald-500/50 shadow-[0_20px_80px_-20px_rgba(16,185,129,0.28)] lg:scale-[1.02]' : 'bg-white border border-slate-200/70 hover:border-slate-300 transition-colors'}`}
                  data-testid={`pricing-${p.slug}`}
                >
                  {p.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest bg-emerald-400 text-white shadow-lg">
                        <Star className="h-3 w-3 fill-white" />
                        Most Popular
                      </span>
                    </div>
                  )}
                  {p.slug === 'legend' && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white shadow-lg">
                        <Trophy className="h-3 w-3" />
                        Elite Automation
                      </span>
                    </div>
                  )}

                  <div>
                    <h3 className="font-bold text-lg text-slate-900 flex items-center gap-2">
                      {p.name}
                      {p.slug === 'starter' && <span className="text-[10px] font-medium text-slate-500">14-Day Full Access</span>}
                    </h3>
                    <p className="text-[12px] text-slate-600 mt-1">{p.desc}</p>
                    {p.badge && (
                      <div
                        className="mt-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-600 text-[10px] font-semibold border border-amber-500/40"
                        data-testid="pricing-starter-trial-badge"
                      >
                        <Clock className="h-3 w-3" /> {p.badge}
                      </div>
                    )}

                    <div className="mt-6 mb-6 flex items-baseline gap-1.5">
                      <span className="text-4xl font-bold tracking-tight text-slate-900 tabular-nums">
                        {price === 0 ? 'Free' : `\u20b9${price.toLocaleString('en-IN')}`}
                      </span>
                      {price !== 0 && <span className="text-sm text-slate-500">{p.period}</span>}
                    </div>
                    {annualBilling && p.priceMonthly > 0 && (
                      <p className="text-[11px] text-emerald-600 mb-4 -mt-3">
                        Billed annually · <span className="line-through text-slate-500">₹{p.priceMonthly.toLocaleString('en-IN')}</span>
                      </p>
                    )}

                    <ul className="space-y-2.5 mb-8">
                      {p.features.map((f, fi) => {
                        if (typeof f === 'object' && f !== null && f.kind === 'ai-suite-divider') {
                          return (
                            <li key="ai-suite-divider" className="list-none pt-2 pb-1" data-testid="pricing-legend-ai-suite-badge">
                              <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-gradient-to-r from-violet-500/15 to-teal-500/15 border border-violet-500/40 text-[10px] font-black uppercase tracking-wider">
                                <Sparkles className="h-2.5 w-2.5 text-amber-400" aria-hidden="true" />
                                <span className="bg-gradient-to-r from-violet-300 to-teal-300 bg-clip-text text-transparent">AI Suite</span>
                                <span className="text-slate-500">·</span>
                                <span className="text-slate-600">Coming Soon</span>
                              </div>
                            </li>
                          );
                        }
                        const isRich = typeof f === 'object' && f !== null;
                        const text = isRich ? f.text : f;
                        const iconMap = { Trophy, Video, Target, Bot, Shield, Share2, Sparkles };
                        const Icon = isRich ? (iconMap[f.icon] || CheckCircle) : CheckCircle;
                        const isBold = isRich && f.bold;
                        const chipConfigs = {
                          studiox: { main: Film, bg: 'bg-teal-100', border: 'border-teal-500/50', mainColor: 'text-teal-700' },
                          socialx: { main: Share2, bg: 'bg-violet-100', border: 'border-violet-500/50', mainColor: 'text-violet-700' },
                        };
                        const chipCfg = isRich && f.chip ? chipConfigs[f.chip] : null;
                        return (
                          <li
                            key={text || `feature-${fi}`}
                            className={`flex items-start gap-2.5 text-[13px] leading-relaxed ${isBold ? 'font-semibold text-slate-900' : 'text-slate-700'}`}
                            data-testid={isBold ? `pricing-legend-addon-${f.icon.toLowerCase()}` : undefined}
                          >
                            {chipCfg ? (
                              <span
                                className={`flex-shrink-0 mt-0.5 relative inline-flex items-center justify-center h-5 w-5 rounded-md shadow-sm border ${chipCfg.bg} ${chipCfg.border}`}
                                data-testid={`pricing-legend-${f.chip}-chip`}
                                aria-hidden="true"
                              >
                                <chipCfg.main className={`h-3 w-3 ${chipCfg.mainColor}`} />
                                <Sparkles className="h-2.5 w-2.5 text-amber-400 absolute -top-1 -right-1 drop-shadow-sm" />
                              </span>
                            ) : (
                              <Icon className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                            )}
                            <span>{text}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>

                  <div className="mt-auto pt-2">
                    {p.comingSoon ? (
                      <div
                        className="w-full rounded-2xl py-3 bg-slate-50 text-slate-600 font-black text-[11px] uppercase tracking-widest text-center border border-slate-200/80 select-none flex items-center justify-center gap-1.5"
                        data-testid={`pricing-cta-${p.slug}`}
                      >
                        <Clock className="h-3.5 w-3.5" /> {p.cta}
                      </div>
                    ) : (
                      <Link to="/signup" className="block">
                        <motion.span
                          whileHover={{ y: -2 }}
                          whileTap={{ scale: 0.98 }}
                          className={`w-full h-12 rounded-2xl font-semibold text-[14px] flex items-center justify-center gap-2 transition-all ${p.popular ? 'bg-emerald-400 text-white hover:bg-emerald-300 shadow-[0_10px_30px_-8px_rgba(16,185,129,0.35)]' : 'bg-slate-100/70 text-slate-900 border border-slate-200 hover:bg-slate-100 hover:border-slate-300'}`}
                          data-testid={`pricing-cta-${p.slug}`}
                        >
                          {p.cta}
                          <ArrowRight className="h-4 w-4" />
                        </motion.span>
                      </Link>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>

          <p className="text-center text-[12px] text-slate-500 mt-8">
            Multi-Center Operations included on every plan · No setup fees · Cancel anytime
          </p>

          {/* AI Suite waitlist teaser (kept from Phase 255-AddonsRoadmap) */}
          <Anim className="mt-20 pt-12 border-t border-slate-200/60" delay={0.05}>
            <div className="max-w-5xl mx-auto" data-testid="landing-addons-section">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Milestone className="w-4 h-4 text-emerald-600" />
                <h3 className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-900">
                  What&rsquo;s next on Out-Play
                </h3>
              </div>
              <p className="text-center text-[13px] text-slate-600 mb-10 max-w-md mx-auto">
                Two AI-first add-ons coming to the Legend tier &mdash; designed to turn your academy into a content-publishing machine.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                <motion.div
                  whileHover={{ y: -4 }}
                  className="relative rounded-3xl backdrop-blur-xl bg-white border border-slate-200/70 hover:border-violet-500/50 transition-colors p-6 overflow-hidden"
                  data-testid="addon-card-socialx"
                >
                  <div className="absolute -inset-10 bg-gradient-to-br from-violet-500/20 to-fuchsia-500/10 blur-3xl opacity-40 pointer-events-none" />
                  <div className="relative flex items-start justify-between gap-2 mb-4">
                    <div className="h-11 w-11 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-500 text-slate-900 flex items-center justify-center shadow-lg">
                      <Wand2 className="h-5 w-5" />
                    </div>
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-violet-100 border border-violet-500/50 text-violet-700 text-[9px] font-black uppercase tracking-widest">
                      Coming Soon
                    </span>
                  </div>
                  <h4 className="relative text-lg font-bold text-slate-900 mb-2">SocialX Engine</h4>
                  <p className="relative text-[13px] leading-relaxed text-slate-600">
                    One-click omnichannel fan-out. AI-generated captions, hero imagery, and short-form reels &mdash; published to every academy social channel in a single tap.
                  </p>
                </motion.div>

                <motion.div
                  whileHover={{ y: -4 }}
                  className="relative rounded-3xl backdrop-blur-xl bg-white border border-slate-200/70 hover:border-teal-500/50 transition-colors p-6 overflow-hidden"
                  data-testid="addon-card-studiox"
                >
                  <div className="absolute -inset-10 bg-gradient-to-br from-teal-500/20 to-emerald-500/10 blur-3xl opacity-40 pointer-events-none" />
                  <div className="relative flex items-start justify-between gap-2 mb-4">
                    <div className="h-11 w-11 rounded-2xl bg-gradient-to-br from-teal-500 to-emerald-500 text-slate-900 flex items-center justify-center shadow-lg">
                      <Film className="h-5 w-5" />
                    </div>
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-teal-100 border border-teal-500/50 text-teal-700 text-[9px] font-black uppercase tracking-widest">
                      Coming Soon
                    </span>
                  </div>
                  <h4 className="relative text-lg font-bold text-slate-900 mb-2">StudioX</h4>
                  <p className="relative text-[13px] leading-relaxed text-slate-600">
                    Browser-native production suite. AI super-resolution upscaling to 4K/8K plus a multi-track video, image, and audio editor &mdash; no desktop install.
                  </p>
                </motion.div>
              </div>

              <div
                className="rounded-3xl backdrop-blur-xl bg-white border border-slate-200/70 p-5 sm:p-6 flex flex-col md:flex-row items-center justify-between gap-5"
                data-testid="addon-waitlist-cta"
              >
                <div className="space-y-1 text-center md:text-left">
                  <h4 className="text-sm font-bold text-slate-900">
                    Want early access to the AI add-on suite?
                  </h4>
                  <p className="text-[12px] text-slate-600">
                    Join the beta waiting list &mdash; we&rsquo;ll email you the moment SocialX and StudioX go live.
                  </p>
                </div>

                {addonSubmitted ? (
                  <div
                    data-testid="addon-waitlist-success"
                    className="flex items-center gap-2 text-[12px] font-semibold text-emerald-700 bg-emerald-100/70 border border-emerald-500/50 px-4 py-2.5 rounded-2xl"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Thanks! We&rsquo;ll ping you when the beta opens.
                  </div>
                ) : (
                  <form onSubmit={submitAddonWaitlist} className="flex flex-col sm:flex-row gap-2 w-full md:w-auto" data-testid="addon-waitlist-form">
                    <div className="relative flex-1 md:w-72">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-500" />
                      <Input
                        type="email"
                        required
                        value={addonEmail}
                        onChange={(e) => setAddonEmail(e.target.value)}
                        placeholder="your.academy@email.com"
                        className="pl-9 text-sm bg-slate-50 border-slate-200/80 text-slate-900 placeholder:text-slate-500 focus-visible:border-emerald-500/60"
                        data-testid="addon-waitlist-email-input"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={addonSubmitting}
                      className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-2xl shrink-0 shadow-[0_8px_22px_-6px_rgba(16,185,129,0.4)]"
                      data-testid="addon-waitlist-submit-btn"
                    >
                      {addonSubmitting ? (
                        <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Adding&hellip;</>
                      ) : (
                        <>Notify Me <ArrowRight className="h-3 w-3 ml-1" /></>
                      )}
                    </Button>
                  </form>
                )}
              </div>
            </div>
          </Anim>
        </div>
      </section>

      {/* ===== PHASE 2 · FAQ — Premium accordion ===== */}
      <section
        className="relative py-24 lg:py-32 bg-[#FAFAFB] border-t border-slate-200/60 overflow-hidden"
        data-testid="faq-section"
      >
        <div aria-hidden className="absolute inset-0 -z-10 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-64 bg-emerald-500/[0.03] blur-[100px] rounded-full" />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Anim className="text-center mb-14">
            <p className="text-[11px] font-medium tracking-[0.22em] uppercase text-emerald-600 mb-3">Frequently asked</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight leading-[1.1]">
              Answers before<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600">you sign up.</span>
            </h2>
          </Anim>

          <Accordion type="single" collapsible className="w-full space-y-3">
            {[
              {
                q: 'How long is the free trial and what do I get?',
                a: 'You get full Pro-tier access for 14 days \u2014 no credit card required. Every module (fees, HRMS, CRM, attendance, events) is unlocked from day one so you can migrate your academy data and see the ROI before you decide to upgrade.',
              },
              {
                q: 'Do you support multi-center or multi-sport academies?',
                a: 'Yes. Every plan supports unlimited centers, coaches, batches, and sports. Managers see a unified operations deck; owners get a consolidated P&L across all branches automatically.',
              },
              {
                q: 'How do parents pay fees? Is it truly automated?',
                a: 'Fee bills auto-generate on the 1st of every month. Reminders fire on WhatsApp on day 3 and day 7. Parents pay via UPI, cards, or netbanking through Razorpay. Payments reconcile automatically and receipts are emailed instantly. Chronic non-payers can be auto-suspended per your rules.',
              },
              {
                q: 'What is StreamX and is it included in my plan?',
                a: 'StreamX is our AI-powered broadcast engine \u2014 go live from a single phone in under 60 seconds, get AI-tagged highlights, and a 90-day replay vault. Pro tier includes a free pilot allowance. Legend tier includes unlimited StreamX + Video Analysis + 8K upscaling (StudioX \u2014 coming soon).',
              },
              {
                q: 'How does ArenaX pricing work for facility bookings?',
                a: 'Independent hosts pay an industry-leading 3.5% flat marketplace fee per booking (payment gateway inclusive). Academies on the Legend tier pay 0% commission, forever \u2014 that is the fastest way to break even on your subscription in a busy season.',
              },
              {
                q: 'Is my academy data secure?',
                a: 'We use enterprise-grade encryption at rest and in transit, role-based access control (RBAC) on every endpoint, and a full audit-log of every admin action. Servers are hosted in India-region and comply with the DPDP Act 2023. Nightly encrypted backups are retained for 30 days.',
              },
              {
                q: 'Can I import my existing student list?',
                a: 'Yes. Every plan includes free onboarding assistance \u2014 our team migrates your existing student, coach, and fee data from spreadsheets or your current CRM. Typical migration takes under 48 hours.',
              },
              {
                q: 'What happens after the 14-day trial ends?',
                a: 'Your data stays exactly where it is. You choose whether to continue on Pro (\u20b9999/mo) or drop to the free Starter view. Nothing is deleted, ever. You can export everything to Excel or PDF anytime.',
              },
            ].map((item, i) => (
              <motion.div
                key={item.q}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
              >
                <AccordionItem
                  value={`faq-${i}`}
                  data-testid={`faq-item-${i}`}
                  className="rounded-2xl backdrop-blur-xl bg-white border border-slate-200/70 hover:border-emerald-500/40 transition-colors overflow-hidden data-[state=open]:border-emerald-500/60"
                >
                  <AccordionTrigger className="px-5 lg:px-6 py-5 text-[15px] font-semibold text-slate-900 hover:no-underline hover:text-emerald-700 transition-colors">
                    <span className="text-left pr-4">{item.q}</span>
                  </AccordionTrigger>
                  <AccordionContent className="px-5 lg:px-6 pb-5 text-[14px] leading-relaxed text-slate-600">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              </motion.div>
            ))}
          </Accordion>

          <Anim className="mt-10 text-center" delay={0.15}>
            <p className="text-[13px] text-slate-600 mb-3">Still have questions?</p>
            <a
              href="#contact"
              data-testid="faq-contact-cta"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl backdrop-blur-xl bg-slate-100/70 hover:bg-slate-100 border border-slate-200 hover:border-emerald-500/50 text-slate-900 font-medium text-[13px] transition-colors"
            >
              <Mail className="h-4 w-4" />
              Talk to our team
              <ArrowRight className="h-4 w-4" />
            </a>
          </Anim>
        </div>
      </section>

      {/* ===== PHASE 2 · CONTACT — Premium dark ===== */}
      <section
        id="contact"
        className="relative py-24 lg:py-32 bg-[#FAFAFB] border-t border-slate-200/60 overflow-hidden"
        data-testid="contact-section"
      >
        <div aria-hidden className="absolute inset-0 -z-10 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-[600px] h-[400px] rounded-full bg-emerald-500/[0.10] blur-[130px]" />
          <div className="absolute bottom-0 right-1/4 w-[500px] h-[400px] rounded-full bg-teal-500/[0.10] blur-[120px]" />
        </div>

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[1fr_1.1fr] gap-10 lg:gap-16 items-start">
          <Anim>
            <p className="text-[11px] font-medium tracking-[0.22em] uppercase text-emerald-600 mb-3">Get in touch</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight leading-[1.1] mb-5">
              Let&rsquo;s build the OS<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600">for your academy.</span>
            </h2>
            <p className="text-[15px] text-slate-600 leading-relaxed mb-8 max-w-md">
              Whether you&rsquo;re running a 50-student academy or a multi-center federation, we&rsquo;d love to hear from you.
            </p>

            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-2xl bg-emerald-100/70 border border-emerald-500/40 flex items-center justify-center">
                  <Mail className="h-5 w-5 text-emerald-700" />
                </div>
                <div>
                  <p className="text-[12px] font-medium text-slate-500 uppercase tracking-[0.12em]">Email us</p>
                  <a href="mailto:contact@out-play.in" className="text-sm text-slate-900 hover:text-emerald-700 transition-colors font-semibold">contact@out-play.in</a>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-2xl bg-cyan-100/70 border border-cyan-500/40 flex items-center justify-center">
                  <AlarmClock className="h-5 w-5 text-cyan-700" />
                </div>
                <div>
                  <p className="text-[12px] font-medium text-slate-500 uppercase tracking-[0.12em]">Response time</p>
                  <p className="text-sm text-slate-900 font-semibold">Within 24 hours</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-2xl bg-violet-100/70 border border-violet-500/40 flex items-center justify-center">
                  <Globe className="h-5 w-5 text-violet-700" />
                </div>
                <div>
                  <p className="text-[12px] font-medium text-slate-500 uppercase tracking-[0.12em]">Serving academies across</p>
                  <p className="text-sm text-slate-900 font-semibold">India · Made with ❤️ in Bengaluru</p>
                </div>
              </div>
            </div>
          </Anim>

          <Anim delay={0.15}>
            <div className="relative rounded-3xl backdrop-blur-xl bg-white border border-slate-200/70 p-6 lg:p-8 overflow-hidden">
              <div aria-hidden className="absolute -inset-10 bg-gradient-to-br from-emerald-500/15 to-teal-500/10 blur-3xl opacity-40 pointer-events-none" />
              <div className="relative">
                <ContactForm source="landing_contact" />
              </div>
            </div>
          </Anim>
        </div>
      </section>

      {/* ===== PHASE 2 · FINAL CTA — Premium gradient band ===== */}
      <section
        className="relative py-24 lg:py-32 overflow-hidden bg-[#FAFAFB]"
        data-testid="final-cta-section"
      >
        <div aria-hidden className="absolute inset-0 pointer-events-none">
          <motion.div
            className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-[500px] bg-gradient-to-r from-emerald-200/40 via-teal-200/40 to-emerald-200/40 blur-[120px]"
            animate={{ opacity: [0.5, 0.8, 0.5] }}
            transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
          />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="relative rounded-[2rem] overflow-hidden p-10 lg:p-16 text-center border border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-teal-50 backdrop-blur-2xl shadow-[0_30px_80px_-24px_rgba(16,185,129,0.24)]"
          >
            <div aria-hidden className="absolute inset-0 opacity-[0.06] bg-[linear-gradient(rgba(15,23,42,0.4)_1px,transparent_1px),linear-gradient(90deg,rgba(15,23,42,0.4)_1px,transparent_1px)] bg-[size:48px_48px]" />

            <motion.div
              initial={{ scale: 0, rotate: -30 }}
              whileInView={{ scale: 1, rotate: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
              className="relative mx-auto w-14 h-14 rounded-2xl bg-emerald-400 text-white flex items-center justify-center shadow-[0_0_40px_-8px_rgba(52,211,153,0.7)] mb-6"
            >
              <Trophy className="h-7 w-7" />
            </motion.div>

            <h2
              className="relative text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight leading-[1.05]"
              data-testid="final-cta-headline"
            >
              Give your academy<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600">its unfair advantage.</span>
            </h2>
            <p className="relative mt-5 text-[15px] lg:text-base text-slate-700 max-w-xl mx-auto">
              14 days of full Pro access. No credit card. No commitment. Just the operating system that turns your academy into a compounding business.
            </p>

            <div className="relative mt-9 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link to="/signup" className="w-full sm:w-auto" data-testid="final-cta-primary">
                <motion.span
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  className="group inline-flex w-full sm:w-auto items-center justify-center gap-2 h-14 px-8 rounded-2xl font-semibold text-[15px] text-white bg-emerald-600 hover:bg-emerald-500 shadow-[0_12px_36px_-8px_rgba(16,185,129,0.4)] transition-colors"
                >
                  Start Free — 14 Days
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                </motion.span>
              </Link>
              <a href="#contact" className="w-full sm:w-auto" data-testid="final-cta-secondary">
                <motion.span
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  className="inline-flex w-full sm:w-auto items-center justify-center gap-2 h-14 px-8 rounded-2xl font-semibold text-[15px] text-slate-900 backdrop-blur-xl bg-slate-100/70 hover:bg-slate-100 border border-slate-200 hover:border-slate-300 transition-colors"
                >
                  <Mail className="h-4 w-4" />
                  Book a demo
                </motion.span>
              </a>
            </div>

            <p className="relative mt-5 text-[12px] text-slate-500">
              Trusted by 1,600+ academies · 40+ sports supported · 99.9% uptime
            </p>
          </motion.div>
        </div>
      </section>

      {/* ===== PHASE 2 · FOOTER — Premium dark, 4-column + newsletter ===== */}
      <footer className="relative bg-[#F5F7FA] border-t border-slate-200/60 pt-16 pb-8" data-testid="landing-footer">
        <div aria-hidden className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-emerald-400/60 to-transparent" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Newsletter row */}
          <div className="rounded-3xl backdrop-blur-xl bg-white border border-slate-200/70 p-6 lg:p-8 mb-14 flex flex-col md:flex-row items-center justify-between gap-6" data-testid="landing-footer-newsletter">
            <div className="text-center md:text-left">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100/70 border border-emerald-500/40 text-emerald-700 text-[10px] font-semibold uppercase tracking-widest mb-3">
                <Send className="h-3 w-3" />
                Product newsletter
              </div>
              <h3 className="text-xl lg:text-2xl font-bold text-slate-900 tracking-tight">
                Get product updates <span className="text-emerald-700">once a month.</span>
              </h3>
              <p className="text-[13px] text-slate-600 mt-1.5">Ship notes, playbooks and academy growth ideas — straight to your inbox. No spam.</p>
            </div>
            {newsletterSubmitted ? (
              <div
                data-testid="landing-footer-newsletter-success"
                className="flex items-center gap-2 text-[13px] font-semibold text-emerald-700 bg-emerald-100/70 border border-emerald-500/50 px-4 py-3 rounded-2xl"
              >
                <CheckCircle className="h-4 w-4" />
                Thanks! You&rsquo;re on the list.
              </div>
            ) : (
              <form onSubmit={submitNewsletter} className="flex flex-col sm:flex-row gap-2 w-full md:w-auto" data-testid="landing-footer-newsletter-form">
                <div className="relative flex-1 md:w-80">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-500" />
                  <Input
                    type="email"
                    required
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    placeholder="you@academy.com"
                    className="pl-9 h-12 rounded-2xl text-sm bg-slate-50 border-slate-200/80 text-slate-900 placeholder:text-slate-500 focus-visible:border-emerald-500/60"
                    data-testid="landing-footer-newsletter-email"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={newsletterSubmitting}
                  className="h-12 px-6 rounded-2xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold shadow-[0_8px_22px_-6px_rgba(16,185,129,0.4)] shrink-0"
                  data-testid="landing-footer-newsletter-submit"
                >
                  {newsletterSubmitting ? (
                    <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Sending&hellip;</>
                  ) : (
                    <>Subscribe <ArrowRight className="h-3 w-3 ml-1" /></>
                  )}
                </Button>
              </form>
            )}
          </div>

          {/* Columns */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 lg:gap-10">
            <div className="col-span-2">
              <div className="flex items-center gap-2.5 mb-4">
                <img src={LOGO_URL} alt="Out-Play" className="h-10 w-10 rounded-xl object-cover ring-2 ring-emerald-500/40" />
                <span className="font-bold text-xl tracking-tight text-slate-900">
                  Out<span className="text-emerald-600">-Play</span>
                </span>
              </div>
              <p className="text-[13px] text-slate-600 leading-relaxed max-w-sm mb-5">
                The operating system for modern sports academies. Purpose-built for India, obsessed with your growth.
              </p>
              <div className="flex items-center gap-2">
                {[
                  { href: 'https://facebook.com/outplayindia', label: 'Facebook', svg: 'M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z' },
                  { href: 'https://twitter.com/outplay_india', label: 'X (Twitter)', svg: 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z' },
                  { href: 'https://instagram.com/outplay.in', label: 'Instagram', svg: 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z' },
                  { href: 'https://youtube.com/@outplayindia', label: 'YouTube', svg: 'M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z' },
                  { href: 'https://linkedin.com/company/outplay-tech', label: 'LinkedIn', svg: 'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z' },
                ].map((s) => (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={s.label}
                    data-testid={`footer-social-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                    className="h-9 w-9 rounded-xl backdrop-blur bg-slate-50 border border-slate-200/80 hover:border-emerald-500/50 hover:bg-emerald-100/70 flex items-center justify-center transition-colors group"
                  >
                    <svg className="h-4 w-4 fill-zinc-400 group-hover:fill-emerald-300 transition-colors" viewBox="0 0 24 24">
                      <path d={s.svg} />
                    </svg>
                  </a>
                ))}
              </div>
            </div>

            <div>
              <h4 className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-900 mb-4">Product</h4>
              <ul className="space-y-2.5 text-[13px] text-slate-600">
                <li><Link to="/features" className="hover:text-slate-900 transition-colors">Features</Link></li>
                <li><Link to="/arenax" className="hover:text-slate-900 transition-colors">ArenaX</Link></li>
                <li><Link to="/streamx" className="hover:text-slate-900 transition-colors">StreamX</Link></li>
                <li><a href="#pricing" className="hover:text-slate-900 transition-colors">Pricing</a></li>
                <li><Link to="/signup" className="hover:text-slate-900 transition-colors">Free Trial</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-900 mb-4">Company</h4>
              <ul className="space-y-2.5 text-[13px] text-slate-600">
                <li><Link to="/faq" className="hover:text-slate-900 transition-colors">FAQ</Link></li>
                <li><a href="#contact" className="hover:text-slate-900 transition-colors">Contact</a></li>
                <li><a href="#" className="hover:text-slate-900 transition-colors">Blog</a></li>
                <li><a href="#" className="hover:text-slate-900 transition-colors">Careers</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-900 mb-4">Legal</h4>
              <ul className="space-y-2.5 text-[13px] text-slate-600">
                <li><Link to="/privacy" className="hover:text-slate-900 transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-slate-900 transition-colors">Terms of Service</Link></li>
                <li><Link to="/faq" className="hover:text-slate-900 transition-colors">Help Center</Link></li>
                <li><a href="mailto:contact@out-play.in" className="hover:text-slate-900 transition-colors">contact@out-play.in</a></li>
              </ul>
            </div>
          </div>

          <div className="mt-14 pt-6 border-t border-slate-200/60 flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-[12px] text-slate-500">
              © 2026 Out-Play.in · All rights reserved.
            </p>
            <div className="flex items-center gap-5 text-[12px] text-slate-500">
              <span className="inline-flex items-center gap-1.5">
                <Shield className="h-3.5 w-3.5 text-emerald-600" />
                Razorpay Secure
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Globe className="h-3.5 w-3.5 text-emerald-600" />
                Made in India
              </span>
              <span className="hidden sm:inline-flex items-center gap-1.5">
                <Sparkles className="h-3.5 w-3.5 text-emerald-600" />
                Built with love ❤️
              </span>
            </div>
          </div>
        </div>
      </footer>

      {/* Sticky CTA — light glass strip (Phase 2 light-mode refresh) */}
      <motion.div initial={{ y: 100 }} animate={{ y: sticky ? 0 : 100 }} transition={{ type: 'spring', stiffness: 300, damping: 30 }} className="hidden sm:block fixed bottom-0 left-0 right-0 z-40 border-t border-slate-200 bg-white/90 backdrop-blur-xl shadow-[0_-8px_32px_-8px_rgba(15,23,42,0.12)] py-3 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <span className="inline-flex items-center justify-center h-9 w-9 rounded-xl bg-emerald-100 border border-emerald-200 flex-shrink-0">
              <Sparkles className="h-4 w-4 text-emerald-600" />
            </span>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-900 leading-tight">Start your 14-day free trial</p>
              <p className="text-[11px] text-slate-500 leading-tight mt-0.5">No credit card required · Setup in 2 minutes</p>
            </div>
          </div>
          <Link to="/signup" className="flex-shrink-0">
            <Button
              size="sm"
              className="h-10 px-5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-semibold shadow-[0_8px_22px_-6px_rgba(16,185,129,0.45)]"
              data-testid="sticky-desktop-cta-btn"
            >
              Get Started <ArrowRight className="ml-1 h-3.5 w-3.5" />
            </Button>
          </Link>
        </div>
      </motion.div>

      {/* Contact Dialog */}
      <Dialog open={dlg} onOpenChange={setDlg}><DialogContent className="max-w-md"><DialogHeader><DialogTitle className="flex items-center gap-2"><Trophy className="h-5 w-5 text-violet-600" /> Contact Sales · Legend Plan</DialogTitle></DialogHeader><p className="text-xs text-muted-foreground mb-2">Tell us about your federation or multi-sport hub for a custom Legend Plan quote.</p><ContactForm source="legend_contact" onSuccess={() => setDlg(false)} buttonText="Request Legend Quote" /></DialogContent></Dialog>

      {/* Phase 255d — AI Suite hero preview side-panel. Mirrors the SocialX
          + StudioX chips merchandised on the Legend tier pricing card and
          deep-links the addon waitlist form for one-tap sign-ups. */}
      <Sheet open={aiSuiteOpen} onOpenChange={setAiSuiteOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" data-testid="ai-suite-sheet">
          <SheetHeader className="text-left">
            <SheetTitle className="flex items-center gap-2 font-display">
              <span className="inline-flex items-center justify-center h-7 w-7 rounded-lg bg-gradient-to-br from-violet-100 to-teal-100 dark:from-violet-950/50 dark:to-teal-950/50 border border-violet-200 dark:border-violet-800">
                <Sparkles className="h-4 w-4 text-amber-500" />
              </span>
              <span className="bg-gradient-to-r from-violet-700 to-teal-700 bg-clip-text text-transparent">
                AI Suite Preview
              </span>
            </SheetTitle>
            <SheetDescription className="text-xs">
              Two AI-first add-ons shipping to the <strong>Legend tier</strong> &mdash; designed to turn your academy into a content-publishing machine.
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6 space-y-4">
            {/* SocialX summary card */}
            <Card className="border-violet-200/70 dark:border-violet-800/40" data-testid="ai-suite-card-socialx">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <span className="relative inline-flex items-center justify-center h-9 w-9 rounded-lg bg-violet-50 dark:bg-violet-950/40 border border-violet-200 dark:border-violet-800 shadow-sm flex-shrink-0">
                    <Share2 className="h-4 w-4 text-violet-600 dark:text-violet-700" />
                    <Sparkles className="h-3 w-3 text-amber-400 dark:text-amber-600 absolute -top-1 -right-1 drop-shadow-sm" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-display font-bold text-sm">SocialX Engine</h4>
                      <Badge variant="outline" className="text-[9px] font-black uppercase tracking-wider bg-slate-50 dark:bg-slate-900">Coming Soon</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
                      1-click omnichannel fan-out. AI captions, hero imagery & short-form reels published to every academy social channel in a single tap.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* StudioX summary card */}
            <Card className="border-teal-200/70 dark:border-teal-800/40" data-testid="ai-suite-card-studiox">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <span className="relative inline-flex items-center justify-center h-9 w-9 rounded-lg bg-teal-50 dark:bg-teal-950/40 border border-teal-200 dark:border-teal-800 shadow-sm flex-shrink-0">
                    <Film className="h-4 w-4 text-teal-600 dark:text-teal-700" />
                    <Sparkles className="h-3 w-3 text-amber-400 dark:text-amber-600 absolute -top-1 -right-1 drop-shadow-sm" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-display font-bold text-sm">StudioX</h4>
                      <Badge variant="outline" className="text-[9px] font-black uppercase tracking-wider bg-slate-50 dark:bg-slate-900">Coming Soon</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
                      Browser-native production suite. AI super-resolution upscaling to 4K/8K plus a multi-track video, image & audio editor &mdash; no desktop install.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Waitlist CTA — closes the sheet, scrolls to the existing addons section. */}
            <Button
              className="w-full rounded-xl bg-gradient-to-r from-violet-600 to-teal-600 hover:from-violet-700 hover:to-teal-700 text-slate-900 shadow-lg"
              data-testid="ai-suite-join-waitlist-btn"
              onClick={() => {
                setAiSuiteOpen(false);
                setTimeout(() => {
                  document.querySelector('[data-testid="landing-addons-section"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }, 200);
              }}
            >
              <Mail className="h-4 w-4 mr-2" /> Join the AI Suite waitlist
            </Button>
            <p className="text-[10px] text-center text-muted-foreground">
              Free Legend tier preview for the first 50 academies who join.
            </p>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
